import { ERecordCommunicationType, EStageNoteType } from '@core/enum/shiksha-deal.enum'
import {
    DATE_INPUT_FORMAT_PLACEHOLDER,
    NAVIGATOR_DATE_DISPLAY_FORMAT,
    NAVIGATOR_DATETIME_DISPLAY_12H,
    NAVIGATOR_DATETIME_DISPLAY_24H,
} from '@app/core/constants/app.constants'
import { ICommunicationTypeOption } from '../models/communication.model'

// Shiksha-specific document upload configuration
const SHIKSHA_DOCUMENT_UPLOAD_CONFIG = {
    SERVICE_NAME: 'lead-management-service',
    KEY: 'shiksha-documents',
} as const
import accountManagersJson from '../../../../assets/data/account-managers.json'
import {
    IAccountManager,
    IAccountManagerOption,
    IAccountManagersData,
} from '../models/shiksha-deal.model'

const accountManagersData = accountManagersJson as IAccountManagersData
export const ACCOUNT_MANAGERS_LIST: IAccountManager[] = accountManagersData.accountManagers
export const ACCOUNT_MANAGER_OPTIONS: IAccountManagerOption[] = ACCOUNT_MANAGERS_LIST.map(
    (accountManager) => ({ value: accountManager.user_id, label: accountManager.name }),
)

const LABEL_CALL = 'Call'
const LABEL_EMAIL = 'Email'
const LABEL_WHATSAPP = 'WhatsApp'
const LABEL_LINKEDIN = 'LinkedIn'
const LABEL_IN_PERSON = 'In Person'
const LABEL_VIRTUAL_MEETING = 'Virtual Meeting'

const STAGE_NOTE_TYPE_LABELS = {
    GENERAL: 'General',
    NEXT_STEPS: 'Next steps',
    RECOMMENDATION: 'Recommendation',
    MISCELLANEOUS: 'Miscellaneous',
} as const
const LABEL_COMMUNICATION_MEDIUM = 'Communication Medium'
const LABEL_CANCEL = 'Cancel'
const LABEL_CLOSE = 'Close'
const LABEL_SAVE = 'Save'
const LABEL_NOTE = 'Note'
const LABEL_YES = 'Yes'
const LABEL_NO = 'No'
import { EApprovalStatus, EDepartment } from '../models/lead-document-approval.model'

const COMMON_COMMUNICATION_FORM_VALUES = {
    TIME_INPUT_MAX_LENGTH: 8,
    TEXTAREA_ROWS: 4,
} as const

const COMMON_COMMUNICATION_FORM_ERROR_MESSAGES = {
    REQUIRED: 'is required',
    DATE_REQUIRED: 'Date is required',
    TIME_REQUIRED: 'Time is required',
    CALL_STATUS_REQUIRED: 'Call status is required',
    CONTACT_PERSON_REQUIRED: 'Contact person is required',
    CALL_NOTE_REQUIRED: 'Call notes are required',
    DURATION_REQUIRED: 'Call duration is required',
    SUBJECT_REQUIRED: 'Subject is required',
    EMAIL_CONTENT_REQUIRED: 'Email content is required',
    CONNECTED_DATE_REQUIRED: 'Connected date is required',
    MESSAGE_SUMMARY_REQUIRED: 'Message summary is required',
    DISCUSSION_NOTE_REQUIRED: 'Discussion note is required',
    TO_REQUIRED: 'At least one recipient is required',
    INVALID_DATE: 'must be a valid date',
    INVALID_TIME: 'must be in valid time format (2:30 PM or 14:30)',
    INVALID_EMAIL: 'must be a valid email address',
    minValue: (min: number) => `must be at least ${min}`,
    maxValue: (max: number) => `must be no more than ${max}`,
    minLength: (length: number) => `Note must be at least ${length} characters`,
    maxLength: (length: number) => `must be no more than ${length} characters`,
    INVALID_FORMAT: 'format is invalid',
    INVALID: 'is invalid',
    DATE_MIN: 'must be on or after the minimum date',
    DATE_MAX: 'must be on or before the current date',
    DATE_FILTER: 'is not a valid date selection',
} as const

export const COMMUNICATION_FORM_FIELD_NAMES = {
    CALL_FORM: {
        CALL_DATE: 'callDate',
        CALL_TIME: 'callTime',
        CALL_DURATION: 'callDuration',
        CALL_STATUS: 'callStatus',
        CONTACT_PERSON: 'contactPerson',
        CALL_NOTE: 'callNote',
    },
    EMAIL_FORM: {
        DATE: 'date',
        TIME: 'time',
        TO: 'to',
        SUBJECT: 'subject',
        EMAIL_CONTENT: 'emailContent',
        ADDITIONAL_NOTE: 'additionalNote',
    },
    MESSAGE_FORM: {
        CONNECTED_DATE: 'connectedDate',
        CONTACT_PERSON: 'contactPerson',
        MESSAGE_SUMMARY: 'messageSummary',
    },
    IN_PERSON_FORM: {
        DATE: 'date',
        TIME: 'time',
        CONTACT_PERSON: 'contactPerson',
        DISCUSSION_NOTE: 'discussionNote',
    },
    VIRTUAL_MEETING_FORM: {
        MEETING_PLATFORM: 'meetingPlatform',
        CUSTOM_PLATFORM_NAME: 'customPlatformName',
        DATE: 'date',
        TIME: 'time',
        CONTACT_PERSON: 'contactPerson',
        DISCUSSION_NOTE: 'discussionNote',
    },
} as const

export interface IStageNoteTypeOption {
    value: EStageNoteType
    label: string
}

// Shared constants reused across the configuration
const SHARED_CONSTANTS = {
    LOCALE_EN_GB: 'en-GB',
    MAX_WIDTH_90VW: '90vw',
    WIDTH_600PX: '600px',
    FORM_FIELD_FILE: 'file',
} as const

// Communication form validation limits (used in DEAL_DETAIL.CALL_FORM.VALUES)
export const COMMUNICATION_FORM_VALIDATION_LIMITS = {
    DURATION_MIN_MINUTES: 1,
    DURATION_MAX_MINUTES: 120,
    NOTE_MAX_LENGTH: 500,
    MESSAGE_SUMMARY_MAX_LENGTH: 500,
    EMAIL_ADDITIONAL_NOTE_MAX_LENGTH: 500,
    EMAIL_SUBJECT_MAX_LENGTH: 200,
    EMAIL_CONTENT_MAX_LENGTH: 1500,
    RECORD_EMAIL_MODAL_EMAIL_BODY_MAX_LENGTH: 1000,
    RECORD_EMAIL_MODAL_DEFAULT_INTERACTION_DURATION_MINUTES: 30,
    HOURS_PER_DAY: 24,
    MINUTES_PER_HOUR: 60,
    TIME_INTERVAL_MINUTES: 30,
} as const

// Keyboard keys that should be blocked for duration input
export const DURATION_INVALID_INPUT_KEYS = ['-', '+', 'e', 'E'] as const

// Regex for keys to block in numeric/amount inputs (includes decimal point)
export const NUMERIC_INVALID_KEY_PATTERN = /^[-+eE.]$/

// Priority order for validation error display (most specific errors first)
export const VALIDATION_ERROR_PRIORITY_ORDER = [
    'matDatepickerParse',
    'matDatepickerMin',
    'matDatepickerMax',
    'matDatepickerFilter',
    'invalidTimeFormat',
    'pattern',
    'email',
    'min',
    'max',
    'minlength',
    'maxlength',
] as const

export const SHIKSHA_CONTACT_PHONE_MIN_DIGITS = 7
export const SHIKSHA_CONTACT_PHONE_MAX_DIGITS = 15
export const SHIKSHA_CONTACT_PHONE_DIGITS_ONLY_PATTERN = /^\d+$/

/** Add Milestone modal: intro for contract value + milestone rows (info banner + disabled-section hint). */
const MILESTONE_CONTRACT_HELPER = 'Enter the contract value and define payment milestones.'

/** Direct Sales UAT — shared copy for notes, communications, POC, and approval remarks */
const COMMON_NOTES = {
    NOTE_CLIENT:
        "Describe the client's business, their key priorities, and why we are engaging with them. e.g., Mid-sized ed-tech company focused on K-12 learning; looking to improve student outcomes through adaptive learning; exploring Mindspark for 30+ schools.",
    NOTE_DEAL:
        'What is the client looking for, and what are we trying to sell in this deal? e.g., Client exploring Mindspark for Grades 6–8 across 20 schools; proposing pilot for AY 2026–27.',
    NOTE_STAGE:
        'Add a brief update relevant to this stage (e.g., Completed demo; client interested in Maths module; follow-up scheduled next week).',
    COMMUNICATION_ALL:
        'Summarize the conversation, key outcomes, and next steps (e.g., Shared pricing details; client asked for proposal; will follow up on Monday).',
    REMARKS_COMMENTED:
        "This status indicates feedback was shared. Add the approver's comments (e.g., Pricing needs revision; include pilot scope details).",
    REMARKS_REJECTED:
        'This proposal was rejected. Add the reason and feedback (e.g., Pricing too high; revise scope and resubmit).',
    POC_ONLY_ONE:
        'Only one contact can be the Point of Contact. Changing this will remove the current Point of Contact and assign it to this contact.',
    POC_IS_CURRENT_SUFFIX:
        ' is currently the Point of Contact. This will replace them with this contact.',
} as const

/** Shared copy for record communication flows (call / email / message / in-person). */
const RECORD_COMMUNICATION_SHARED_PLACEHOLDERS = {
    SELECT_DATE: DATE_INPUT_FORMAT_PLACEHOLDER,
    SELECT_CONTACT_PERSON: 'Select contact person',
    NO_CONTACTS_AVAILABLE: 'No contacts available',
} as const

// Shiksha Funder Detail feature constants - centralized configuration
export const SHIKSHA_FUNDER_DETAIL_CONSTANTS = {
    UAT: {
        // Minimum characters required for all note/textarea fields in Shiksha module
        NOTE_MIN_LENGTH: 20,
        // Placeholders for note fields (client creation, deals, stage notes, communication notes)
        NOTE_PLACEHOLDERS: {
            CLIENT_CREATION_NOTE: COMMON_NOTES.NOTE_CLIENT,
            DEAL_CREATION_NOTE: COMMON_NOTES.NOTE_DEAL,
            STAGE_NOTE: COMMON_NOTES.NOTE_STAGE,
            COMMUNICATION: {
                CALL_NOTE: COMMON_NOTES.COMMUNICATION_ALL,
                EMAIL_CONTENT: COMMON_NOTES.COMMUNICATION_ALL,
                MESSAGE_SUMMARY: COMMON_NOTES.COMMUNICATION_ALL,
                IN_PERSON_DISCUSSION: COMMON_NOTES.COMMUNICATION_ALL,
                VIRTUAL_MEETING_DISCUSSION: COMMON_NOTES.COMMUNICATION_ALL,
            },
        },
        CONTACT_NAME_VALIDATION: {
            MIN_LENGTH: 2,
            // Keep as a descriptive string; the actual validator logic lives in utils.
            PATTERN_DESCRIPTION:
                'Name should contain alphabets and common separators (spaces, dots, dashes).',
        },
        MOBILE_NUMBER_VALIDATION: {
            // Contacts: international-friendly subscriber length (NAV-7834; ITU-T E.164 max 15 digits)
            MIN_DIGITS: SHIKSHA_CONTACT_PHONE_MIN_DIGITS,
            MAX_DIGITS: SHIKSHA_CONTACT_PHONE_MAX_DIGITS,
            ERROR_MESSAGE: 'Please enter a valid mobile number (7–15 digits)',
        },
        LINKEDIN_URL_PLACEHOLDER: 'https://www.linkedin.com/in/profile-name',
        WEBSITE_URL_PLACEHOLDER: 'https://www.example.com',
    },
    DEAL_FORM: {
        MAX_DEAL_PROJECTIONS: 999999999,
        MAX_ESTIMATED_AMOUNT: 999999999,
        MAX_NOTE_LENGTH: 2500,
        DEFAULT_STAGE_ID: 0,
        DEAL_TYPE: {
            DIRECT_SALES: 'Direct Sales',
            TENDER: 'Tender',
            DIRECT_SALES_VALUE: 'direct-sales',
            TENDER_VALUE: 'tender',
        },
        OPPORTUNITY_TYPE: {
            NEW: 'new',
            CROSS_SELL: 'cross-sell',
            UPSELL: 'upsell',
            DEFAULT_LABEL: 'Select Opportunity Type',
            LABELS: {
                NEW: 'New',
                CROSS_SELL: 'Cross-sell',
                UPSELL: 'Upsell',
            },
            OPTIONS: [
                { value: 'new', label: 'New' },
                { value: 'cross-sell', label: 'Cross-sell' },
                { value: 'upsell', label: 'Upsell' },
            ],
        },
        PRODUCTS: [
            { id: 3, value: 'Mindspark', label: 'Mindspark' },
            { id: 4, value: 'Shiksha Assessments', label: 'Shiksha Assessments' },
        ],
        PRODUCT_PRIORITY: {
            'Shiksha Assessments': 1,
            Mindspark: 2,
            DEFAULT_PRIORITY: 999,
        },
        DOMAIN_SIMILARITY_CONFIG: {
            PREFIX_LENGTH: 2,
            SUFFIX_LENGTH: 3,
            MIN_DOMAIN_LENGTH: 5,
            MAX_LENGTH_DIFF: 3,
            MAX_LENGTH_DIFF_FALLBACK: 2,
            MAX_CHAR_DIFF: 2,
            MIN_SIMILARITY_CHARS: 4,
            EMAIL_PARTS_LENGTH: 2,
        },
        EMAIL_VALIDATION_CONFIG: {
            MAX_LENGTH: 150,
            DEFAULT_MAX_LENGTH: 100,
            EMAIL_DOMAIN_TYPE_ERROR: 'emailDomainTypo',
        },
        STATUS: {
            ACTIVE: 'ACTIVE',
            INACTIVE: 'INACTIVE',
        },
        IS_POC_OPTIONS: [
            { value: 'true', label: LABEL_YES },
            { value: 'false', label: LABEL_NO },
        ],
        ENGAGEMENT_TYPES: [
            { value: 'Inbound', label: 'Inbound' },
            { value: 'Outbound', label: 'Outbound' },
        ],
        SOURCES: [
            { value: '', label: 'Select Source' },
            { value: 'Cold calling', label: 'Cold calling' },
            { value: 'Cold emails', label: 'Cold emails' },
            { value: 'Events', label: 'Events' },
            { value: 'Campaigns', label: 'Campaigns' },
            { value: 'LinkedIn Outreach', label: 'LinkedIn Outreach' },
            { value: 'Primary Research', label: 'Primary Research' },
            { value: 'Tender', label: 'Tender' },
        ],
        DEAL_PROJECTIONS_OPTIONS: [
            { value: '', label: 'Select Deal Projections' },
            { value: '0-4999999', label: 'Less than 50 Lakh' },
            { value: '5000000-10000000', label: '₹50L - 1 Cr' },
            { value: '10000001-15000000', label: '>1 Cr - 1.5 Cr' },
            { value: '15000000+', label: '>1.5 Cr' },
        ],
        AMOUNT_IN_WORDS: {
            ONES: [
                '',
                'One',
                'Two',
                'Three',
                'Four',
                'Five',
                'Six',
                'Seven',
                'Eight',
                'Nine',
                'Ten',
                'Eleven',
                'Twelve',
                'Thirteen',
                'Fourteen',
                'Fifteen',
                'Sixteen',
                'Seventeen',
                'Eighteen',
                'Nineteen',
            ],
            TENS: [
                '',
                '',
                'Twenty',
                'Thirty',
                'Forty',
                'Fifty',
                'Sixty',
                'Seventy',
                'Eighty',
                'Ninety',
            ],
            HUNDRED_SUFFIX: ' Hundred',
            CRORE: ' Crore',
            LAKH: ' Lakh',
            THOUSAND: ' Thousand',
            ZERO: 'Zero',
        },
    },
    STAGE: {
        MIN_STAGE_ID: 1,
        MAX_STAGE_ID: 5,
        DEAL_QUALIFICATION_ID: 1,
        PROPOSAL_ID: 2,
        PROPOSAL_ACCEPTED_ID: 3,
        CONTRACT_ACCEPTED_ID: 4,
        DEAL_WON_ID: 5,
        DEAL_DROPPED_ID: 6,
    },
    DEAL_STATUS: {
        INTERESTED: 'interested',
        DROPPED: 'dropped',
        SENT_TO_CLIENT: 'sent_to_client',
        REQUEST_FOR_CHANGES: 'request_for_changes',
        REQUEST_FOR_CHANGE: 'request_for_change',
        ACCEPT_PROPOSAL: 'accept_proposal',
        CONTRACT_ACCEPTED: 'contract_accepted',
        ON_HOLD: 'on_hold',
    },
    DEAL_ON_HOLD_MODAL_MODE: {
        PUT_ON_HOLD: 'put-on-hold',
        REMOVE_FROM_HOLD: 'remove-from-hold',
    },
    DEAL_ON_HOLD_ACTIVITY: {
        MS_PER_DAY: 24 * 60 * 60 * 1000,
        DAYS_WARNING_THRESHOLD: 45,
        DAYS_ERROR_THRESHOLD: 90,
    },
    FILTER: {
        FINANCIAL_YEAR: {
            ALL_LABEL: 'All Financial Years',
            ALL_VALUE: 'all',
            FUTURE_YEARS: 2,
            PAST_YEARS: 2,
        },
        STAGE: {
            ALL_LABEL: 'All Stages',
            ALL_VALUE: 'all',
            OPTIONS: [
                { value: 'all', label: 'All Stages' },
                { value: 's1', label: 'S1 (Deal Qualification)' },
                { value: 's2', label: 'S2 (Proposal)' },
                { value: 's3', label: 'S3 (Engagement)' },
                { value: 's4', label: 'S4 (Project Confirmation)' },
                { value: 's5', label: 'S5 (Deal Won)' },
                { value: 's6', label: 'Deal Lost' },
                { value: 's7', label: 'Deal On Hold' },
            ],
            DISPLAY_NAMES: {
                0: 'Customer Created',
                1: 'Deal Qualified',
                2: 'Proposal Sent',
                3: 'Proposal Accepted',
                4: 'Contract Accepted',
                5: 'Deal Won',
                6: 'Deal Lost',
                7: 'Deal On Hold',
            },
        },
        STATUS_TYPE: {
            ALL_LABEL: 'All Status Types',
            ALL_VALUE: 'all',
            ACTIVE: 'active',
            INACTIVE: 'inactive',
            OPTIONS: [
                { value: 'all', label: 'All Status Types' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
            ],
        },
    },
    VALIDATION: {
        ERROR_MESSAGES: {
            required: (fieldName: string): string => `${fieldName} is required`,
            minValue: (fieldName: string): string =>
                `${fieldName} must be greater than or equal to 0`,
            maxValue: (fieldName: string, maxValue: number): string =>
                `${fieldName} cannot exceed ${maxValue.toLocaleString()}`,
            maxLength: (fieldName: string, maxLength: number): string =>
                `${fieldName} cannot exceed ${maxLength} characters`,
            EMAIL_OR_PHONE_REQUIRED: 'Either email or mobile number is required',
            onlySpaces: (fieldName: string): string => `${fieldName} cannot contain only spaces`,
            PATTERN: {
                EMAIL: 'Please enter a valid email address',
                PHONE: 'Please enter a valid mobile number (7–15 digits)',
                NAME: 'Name can only contain letters, spaces, dots, and dashes',
                LINKEDIN:
                    'Enter a full LinkedIn URL starting with https:// (e.g. https://www.linkedin.com/in/username)',
                invalidFormat: (fieldName: string): string =>
                    `Invalid ${fieldName.toLowerCase()} format`,
            },
        },
        REGEX: {
            BODY_FIELD_PREFIX: /^body\.[a-zA-Z_]+:\s*/i,
        },
    },
    SNACKBAR: {
        DURATION: {
            SUCCESS: 3000,
            ERROR: 5000,
        },
        POSITION: {
            HORIZONTAL: 'center',
            VERTICAL: 'bottom',
        },
        CLASSES: {
            SUCCESS: 'success-snackbar',
            ERROR: 'error-snackbar',
        },
        MESSAGES: {
            DEAL_CREATED: 'Deal created successfully',
            DEAL_CREATION_FAILED: 'Something went wrong while creating deal. Please try again.',
            CONTACT_CREATED: 'Contact created successfully',
            CONTACT_CREATION_FAILED:
                'Something went wrong while creating contact. Please try again.',
            CONTACT_UPDATED: 'Contact updated successfully',
            CONTACT_UPDATE_FAILED: 'Something went wrong while updating contact. Please try again.',
            CONTACT_DELETED: 'Contact deleted successfully.',
            CONTACT_DELETE_FAILED: 'Something went wrong while deleting contact. Please try again.',
            FUNDER_ID_REQUIRED: 'Funder ID is required',
            CALL_RECORDED: 'Call recorded successfully',
            EMAIL_RECORDED: 'Email recorded successfully',
            WHATSAPP_RECORDED: 'WhatsApp message recorded successfully',
            LINKEDIN_RECORDED: 'LinkedIn message recorded successfully',
            IN_PERSON_RECORDED: 'In-person visit recorded successfully',
            VIRTUAL_MEETING_RECORDED: 'Virtual meeting recorded successfully',
            COMMUNICATION_RECORD_FAILED:
                'Something went wrong while recording communication. Please try again.',
            LEAD_ID_REQUIRED: 'Lead ID is required to record communication',
            USER_ID_REQUIRED: 'User ID is required to record communication',
            INVALID_FORM_DATA: 'Invalid form data. Please check all required fields.',
            SERVER_ERROR: 'Something went wrong. Please try again later.',
        },
    },
    DEAL_DETAIL: {
        STICKY_HEADER_OFFSET: 74,
        DIALOG_WIDTH: SHARED_CONSTANTS.WIDTH_600PX,
        DEBOUNCE_TIME: 300,
        BATCH_UPDATE_DEBOUNCE_TIME_MS: 500,
        DEAL_OWNERS_LIMIT: 50,
        STAGE_PREFIX: 'S',
        DEFAULT_STAGE_NUMBER: 0,
        SEARCH_DEBOUNCE_TIME_MS: 300,
        TIMEOUT: {
            SHORT_DELAY_MS: 100,
            MEDIUM_DELAY_MS: 500,
            LONG_DELAY_MS: 15000,
        },
        STAGE_IDS: {
            STAGE_1: 1,
            STAGE_2: 2,
            STAGE_3: 3,
            STAGE_4: 4,
            STAGE_5: 5,
            STAGE_0: 0,
        },
        MESSAGES: {
            LOADING: 'Loading...',
            LOADING_DEAL_INFORMATION: 'Loading deal information...',
            DEAL_NOT_FOUND: 'Deal not found',
            FUNDER_NOT_FOUND: 'No funder found',
            FAILED_TO_LOAD_DEAL: 'Failed to load deal details',
            FAILED_TO_LOAD_FUNDER: 'Failed to load funder details',
            DEAL_REASSIGNED_SUCCESS: 'Deal reassigned successfully',
            FAILED_TO_LOAD_OWNERS: 'Failed to load deal owners. Please try again.',
            FAILED_TO_REASSIGN: 'Failed to reassign deal. Please try again.',
            SERVER_ERROR_REASSIGN: 'Something went wrong while reassigning deal. Please try again.',
            NO_USERS_FOUND: 'No users found',
            LOADING_DEAL_OWNERS: 'Loading deal owners...',
            LOADING_PAYMENT_DETAILS: 'Loading payment details...',
            DOCUMENT_UPLOAD_SUCCESS: 'Document uploaded successfully',
            DOCUMENT_UPLOAD_FAILED: 'Document upload failed. Please try again.',
            APPROVAL_SUBMIT_SUCCESS: 'Approval submitted successfully.',
            APPROVAL_SUBMIT_FAILED: 'Approval submission failed. Please try again.',
            APPROVAL_DELETE_SUCCESS: 'Approval deleted successfully.',
            APPROVAL_DELETE_FAILED: 'Failed to delete approval. Please try again.',
            APPROVAL_EDIT_SUCCESS: 'Approval updated successfully.',
            APPROVAL_EDIT_FAILED: 'Failed to update approval. Please try again.',
            STATUS_REQUIRED: 'Status is required.',
            STATUS_SELECTION_REQUIRED: 'Please select a status before submitting the approval.',
            REASON_REQUIRED_FOR_STATUS: 'Reason is required for this status.',
            REMARKS_REQUIRED_FOR_STATUS: (statusDisplay: string): string =>
                `Remarks are required for ${statusDisplay} status.`,
            DUPLICATE_APPROVER_NOT_ALLOWED:
                'The same approver cannot be selected in multiple rows.',
            APPROVAL_PROOF_REQUIRED: 'Approval proof (attachment) is required for approved status',
            APPROVAL_PROOF_REQUIRED_WHEN_APPROVED:
                'Approval proof attachment is required when status is Approved.',
            DOCUMENT_ID_MISSING: 'Document ID is missing. Please refresh and try again.',
            APPROVED_BY_REQUIRED:
                'Please select Department, Designation, and POC before submitting this approval.',
            INVALID_STATUS_SELECTED:
                'Invalid status selected. Please select a valid status from the dropdown.',
            INVALID_STATUS_WITH_LIST:
                'The selected status is not valid. Please choose from: SENT FOR APPROVAL, Commented, Rejected, or Approved.',
            FAILED_TO_SAVE_APPROVAL_CHANGES: 'Failed to save approval changes. Please try again.',
            FAILED_TO_SUBMIT_APPROVAL: 'Failed to submit approval. Please try again.',
            DOCUMENT_ID_MISSING_CONSOLE: 'Document ID is missing in modal data',
            DOCUMENT_ID_NOT_FOUND: 'Document ID not found in response',
            CURRENT_USER_FALLBACK: 'Current User',
            APPROVED_REMARKS: 'File Approved',
            SENT_FOR_APPROVAL_REMARKS: 'Sent for Approval',
            FILE_ATTACHED: 'File attached',
            UNKNOWN_USER: 'Unknown',
            DEAL_UPDATED_SUCCESS: 'Deal updated successfully',
            DEAL_UPDATED_SUCCESS_WITH_PAYMENT: 'Deal updated successfully with payment',
            REQUIREMENTS_CONFIRMED_SUCCESS: 'Requirements confirmed successfully',
            DEAL_MOVED_TO_STAGE_2_SUCCESS: 'Deal moved to Stage 2 successfully',
            DEAL_DROPPED_SUCCESS: 'Deal lost successfully',
            DEAL_ON_HOLD_SUCCESS: 'Deal put on hold successfully',
            DEAL_RESUMED_SUCCESS: 'Deal removed from hold successfully',
            LEAD_ID_REQUIRED: 'Lead ID is required to save',
            FAILED_TO_UPDATE_DEAL: 'Failed to update deal',
            FAILED_TO_UPDATE_PAYMENT_DETAILS: 'Failed to update payment details',
            FAILED_TO_UPDATE_REQUIREMENTS: 'Failed to update requirements',
            FAILED_TO_MOVE_TO_STAGE_2: 'Failed to move deal to Stage 2',
            FAILED_TO_DROP_DEAL: 'Failed to mark deal as lost',
            SOMETHING_WENT_WRONG: 'Something went wrong',
        },
        VALUES: {
            YES: 'yes',
            NO: 'no',
            DONT_KNOW: 'dont_know',
            NA: 'na',
            NA_ALIASES: ['n/a', 'na'],
        },
        DATE: {
            FORMAT_DD_MM_YYYY: 'dd-MM-yyyy',
            FORMAT_DD_MMM_YYYY: NAVIGATOR_DATE_DISPLAY_FORMAT,
            FORMAT_DATETIME_STAGE_NOTE: NAVIGATOR_DATETIME_DISPLAY_12H,
            FORMAT_DATETIME_COMMUNICATION: NAVIGATOR_DATETIME_DISPLAY_12H,
        },
        ERROR_MATCHERS: {
            NO_DEALS_FOUND: 'no deals found',
        },
        SKELETON: {
            LEFT: {
                TITLE: { showHeader: false, lines: 1, lineWidths: ['l'], showActions: false },
                STAGE_BLOCK: {
                    showHeader: true,
                    headerWidth: 'l',
                    showSubheader: false,
                    lines: 10,
                    lineWidths: ['m', 'm', 'm', 's', 'm', 'm', 'm', 'm', 'm', 'xs'],
                    showActions: false,
                },
                NOTES: { showHeader: false, lines: 2, lineWidths: ['s', 'xs'], showActions: false },
                STAGES: [1, 2],
            },
            RIGHT: {
                UPPER: {
                    showHeader: true,
                    headerWidth: 'm',
                    showSubheader: false,
                    lines: 6,
                    lineWidths: ['full', 'm', 'l', 'full'],
                    showActions: false,
                },
                LOWER: {
                    showHeader: true,
                    headerWidth: 'm',
                    showSubheader: false,
                    lines: 3,
                    lineWidths: ['full', 'l', 'm'],
                    showActions: false,
                },
            },
        },
        DOCUMENT_UPLOAD: {
            SERVICE_NAME: SHIKSHA_DOCUMENT_UPLOAD_CONFIG.SERVICE_NAME,
            KEY: SHIKSHA_DOCUMENT_UPLOAD_CONFIG.KEY,
        },
        DOCUMENTS_API: {
            LIST_LIMIT: 50,
            FILTER_DETAILS: 'details',
            COOKIE_TOKEN_NAME: 'token',
            TOKEN_HEADER_NAME: 'token',
            DEFAULT_DOC_URL: '#',
            DEFAULT_STATUS: 'NA',
            DEFAULT_FILE_TYPE: 'application/octet-stream',
            FILE_ACCEPT: '.pdf,.doc,.docx,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png',
            LOCALE_DATE: SHARED_CONSTANTS.LOCALE_EN_GB,
            DEFAULT_DOCUMENT_TYPE: 'Other',
            DOCUMENT_TYPES: ['Proposal', 'Contract', 'Invoice', 'Other'],
            DOCUMENT_TYPE_PROPOSAL: 'Proposal',
            DOCUMENT_TYPE_CONTRACT: 'Contract',
            DOCUMENT_TYPE_INVOICE: 'Invoice',
            DOCUMENT_TYPE_OTHER: 'Other',
            DOCUMENT_TYPE_FOLDERS: {
                Proposal: 'proposal',
                Contract: 'contract',
                Invoice: 'invoice',
                Other: 'other',
            } as const,
            UPLOAD_FORM_FIELDS: {
                FILE: SHARED_CONSTANTS.FORM_FIELD_FILE,
                DOCUMENT_TYPE: 'document_type',
                SERVICE_NAME: 'service_name',
                BUCKET: 'bucket',
                KEY: 'key',
                FILE_NAME: 'file_name',
                FILE_TYPE: 'file_type',
            },
            DETAILS_QUERY_PARAMS: {
                DOCUMENT_ID: 'document_id',
                FILTER: 'filter',
            },
            VERSION: {
                PREFIX: 'v',
                DEFAULT: 'v1',
            },
            buildUrl: (apiUrl: string, leadId: number): string =>
                `${apiUrl}/api/aggregated/v1/leads/${leadId}/documents`,
            APPROVAL_STATUS_OPTIONS: [
                EApprovalStatus.SENT_FOR_APPROVAL,
                EApprovalStatus.COMMENTED,
                EApprovalStatus.REJECTED,
                EApprovalStatus.APPROVED,
            ] as const,
            STATUSES_REQUIRING_REASON: [
                EApprovalStatus.REJECTED,
                EApprovalStatus.COMMENTED,
            ] as const,
            REASON_MAX_LENGTH: 500,
            DEPARTMENT_OPTIONS: [
                EDepartment.FINANCE,
                EDepartment.SHIKSHA,
                EDepartment.OPERATIONS,
                EDepartment.LEGAL,
            ] as const,
            DESIGNATION_OPTIONS: [
                'Associate Manager-Growth and Partnerships',
                'Ei Shiksha',
                'Lead, Growth & Partnerships',
                'Manager - Growth & Partnerships',
                'Senior Manager - Finance & Accounts',
                'Senior Vice President',
                'Senior Manager',
                'Manager',
                'AVP, Director of Operations at Ei Shiksha',
                'Financial Controller',
                'Manager - Finance & Accounts',
                'Manager - Project Management',
                'Senior Manager - Finance',
                'Associate Vice President - EI Shiksha',
            ] as const,
            POC_OPTIONS: [
                {
                    name: 'Rajvi Thakkar',
                    email: 'rajvi.thakkar@ei.study',
                    department: EDepartment.FINANCE,
                    designation: 'Senior Manager - Finance & Accounts',
                },
                {
                    name: 'Adarsh A',
                    email: 'adarsh.a@ei.study',
                    department: EDepartment.SHIKSHA,
                    designation: 'Associate Manager-Growth and Partnerships',
                },
                {
                    name: 'Indulekha S',
                    email: 'indulekha.s@ei.study',
                    department: EDepartment.SHIKSHA,
                    designation: 'Manager - Growth & Partnerships',
                },
                {
                    name: 'Sanjana Saklani',
                    email: 'sanjana.saklani@ei.study',
                    department: EDepartment.SHIKSHA,
                    designation: 'Lead, Growth & Partnerships',
                },
                {
                    name: 'Shreya Agrawal',
                    email: 'shreya.agrawal@ei.study',
                    department: EDepartment.SHIKSHA,
                    designation: 'Manager - Growth & Partnerships',
                },
                {
                    name: 'Siri Shetty',
                    email: 'siri.shetty@ei.study',
                    department: EDepartment.SHIKSHA,
                    designation: 'Associate Manager-Growth and Partnerships',
                },
                {
                    name: 'Vivek Sehgal',
                    email: 'vivek.sehgal@ei.study',
                    department: EDepartment.SHIKSHA,
                    designation: 'Senior Vice President',
                },
                {
                    name: 'Gayatri Vaidya',
                    email: 'gayatri.vaidya@ei.study',
                    department: EDepartment.SHIKSHA,
                    designation: 'Associate Vice President - EI Shiksha',
                },
                {
                    name: 'Pradeep Mahi Pal Kumar',
                    email: 'pradeep.kumar1@ei.study',
                    department: EDepartment.OPERATIONS,
                    designation: 'AVP, Director of Operations at Ei Shiksha',
                },
                {
                    name: 'Nitin Chandel',
                    email: 'nitin.chandel@ei.study',
                    department: EDepartment.OPERATIONS,
                    designation: 'Senior Manager',
                },
                {
                    name: 'Nitish Kumar',
                    email: 'nitish.kumar@ei.study',
                    department: EDepartment.OPERATIONS,
                    designation: 'Manager',
                },
                {
                    name: 'Naman Kumar',
                    email: 'naman.kumar@ei.study',
                    department: EDepartment.OPERATIONS,
                    designation: 'Manager - Project Management',
                },
                {
                    name: 'Sachin Sahni',
                    email: 'sachin.sahni@ei.study',
                    department: EDepartment.FINANCE,
                    designation: 'Senior Manager - Finance',
                },
                {
                    name: 'Ashish Tulsyan',
                    email: 'ashish.tulsyan@ei.study',
                    department: EDepartment.LEGAL,
                    designation: 'Financial Controller',
                },
                {
                    name: 'Rajan Kumar',
                    email: 'rajan.kumar@ei.study',
                    department: EDepartment.LEGAL,
                    designation: 'Manager - Finance & Accounts',
                },
            ] as const,
            buildApprovalUrl: (apiUrl: string, leadId: number, documentId: number): string =>
                `${apiUrl}/api/aggregated/v1/common-leads/v1/leads/${leadId}/documents/${documentId}/approval`,
            buildApprovalsListUrl: (apiUrl: string, leadId: number, documentId: number): string =>
                `${apiUrl}/api/lead-management/v1/leads/${leadId}/approvals?document_id=${documentId}`,
            buildDeleteApprovalUrl: (
                apiUrl: string,
                leadId: number,
                documentId: number,
                approvalId: number,
            ): string =>
                `${apiUrl}/api/lead-management/v1/leads/${leadId}/documents/${documentId}/approvals/${approvalId}`,
            buildEditApprovalUrl: (
                apiUrl: string,
                leadId: number,
                documentId: number,
                approvalId: number,
            ): string =>
                `${apiUrl}/api/aggregated/v1/leads/${leadId}/documents/${documentId}/approvals/${approvalId}`,
            buildDocumentDetailsUrl: (apiUrl: string, leadId: number): string =>
                `${apiUrl}/api/aggregated/v1/leads/${leadId}/documents`,
            buildUploadUrl: (apiUrl: string, leadId: number): string =>
                `${apiUrl}/api/aggregated/v1/common-leads/v1/leads/${leadId}/documents`,
        },
        LABELS: {
            REASSIGN_DEAL: 'Reassign Deal',
            RECORD_COMMUNICATION: 'Record Communication',
            SCHEDULE_VISIT: 'Schedule a Visit',
            REASSIGN: 'Reassign',
            REASSIGNING: 'Reassigning...',
            SEARCH_BY_NAME: 'Search by Name',
            CURRENT_OWNER: 'Current Owner',
            DEALS: 'Deals:',
            LEFT_SIDE_CONTENT: 'Left Side Content',
            RIGHT_SIDE_CONTENT: 'Right Side Content',
            STAGE_5_RIGHT_TITLE: 'Finalized & Secured',
            STAGE_5_RIGHT_MESSAGE:
                'The Deal stage movement is complete, and the record is now preserved in a view-only state with no further actions required.',
            ACTION_REQUIRED: 'Action Required',
            DEAL_INFORMATION_TITLE: 'Deal Information',
            COLLAPSIBLE_BLOCK_TITLE: 'Collapsible Block',
            UPLOAD_FILES_TITLE: 'Documents',
            COLLAPSIBLE_CONTENT: 'Collapsible content will be added here',
            STAGE_5_DEAL_WON_MESSAGE:
                'Deal finalized - contract and payment details are available below.',
            STAGE_5_CONTRACT_VALUE: 'Contract Value',
            STAGE_5_AMOUNT_EXCL_GST: 'Amount (excl. GST)',
            STAGE_5_AMOUNT_INCL_GST: 'Amount (incl. GST)',
            STAGE_5_GST_PERCENT_TAG: '18% GST',
            STAGE_5_VIEW_ALL_MILESTONES: 'View all milestones',
            COMMUNICATION_RECORDS_TITLE: 'Communication Records',
            VIEW_ALL_STAGE_NOTES_TITLE: 'View all stage-wise notes',
            DEAL_STATUS: 'Deal Status',
            DEAL_ON_HOLD: 'Deal On Hold',
            RESUME_DEAL: 'Resume Deal',
            DEAL_ON_HOLD_STATE_MESSAGE:
                'This deal is on hold. Resume the deal to continue working on it.',
            DEAL_ON_HOLD_MODAL_TITLE: 'Put Deal On Hold',
            REMOVE_FROM_HOLD_MODAL_TITLE: 'Remove from Hold',
            REMOVE_FROM_HOLD_MODAL_MESSAGE:
                'Remove this deal from hold? You can put it on hold again later from the deal status menu.',
            REMOVE_FROM_HOLD_CONFIRM: 'Remove from Hold',
            DEAL_ON_HOLD_MODAL_SUBTITLE:
                'Review the following details before putting this deal on hold.',
            LAST_COMMUNICATION: 'Last Communication',
            LAST_STAGE_MOVEMENT: 'Last Stage Movement',
            DEAL_CREATED_AT: 'Deal Created',
            DAYS_SINCE_LAST_ACTIVITY: 'Days since last activity in stage',
            JUST_NOW: 'Just now',
            MINUTES_AGO: (mins: number) => (mins === 1 ? '1 minute ago' : `${mins} minutes ago`),
            HOURS_AGO: (hours: number) => (hours === 1 ? '1 hour ago' : `${hours} hours ago`),
            DAYS_AGO: (days: number) => (days === 1 ? '1 day ago' : `${days} days ago`),
            MONTHS_AGO: (months: number) => (months === 1 ? '1 month ago' : `${months} months ago`),
            YEARS_AGO: (years: number) => (years === 1 ? '1 year ago' : `${years} years ago`),
            CONFIRM_ON_HOLD: 'Confirm',
            CANCEL: 'Cancel',
            PUTTING_ON_HOLD_PROGRESS: 'Putting on hold...',
            REMOVING_FROM_HOLD_PROGRESS: 'Removing from hold...',
            ACTIVITY_NA: '—',
            COMMUNICATION_TYPE_FALLBACK: '—',
            NO_LAST_COMMUNICATION: 'No communication recorded',
            NO_LAST_STAGE_MOVEMENT: 'No stage movement recorded',
            DEAL_STATUS_INTERESTED: 'Interested',
            DEAL_STATUS_SENT_TO_CLIENT: 'Sent to Client',
            DEAL_STATUS_ACCEPT_PROPOSAL: 'Accept Proposal',
            DEAL_STATUS_CONTRACT_ACCEPTED: 'Contract Accepted',
            RECORD_COMMUNICATION_CONTENT: 'Record Communication content will be added here',
            SELECT_COMMUNICATION_TYPE: 'Select Communication Type',
            VISITS_MADE_TITLE: 'Visits Made',
            NO_VISITS_RECORDED_YET: 'No visits recorded yet',
            SCHEDULE_FIRST_VISIT_MESSAGE: 'Schedule your first visit to this lead',
            SHOW_MORE: 'Show More',
            SHOW_LESS: 'Show Less',
            LOADING_COMMUNICATION_RECORDS: 'Loading communication records...',
            NO_COMMUNICATION_RECORDS_FOUND: 'No communication records found',
            NO_COMMUNICATION_RECORDS_MATCH_FILTER:
                'No communication records match your current filter.',
            COMMUNICATION_RECORDS_LIST_LIMIT: 50,
            CALL_NOTES_LABEL: 'CALL NOTES',
            NOTES_LABEL: 'NOTES',
            FOLLOW_UP_SCHEDULED: 'Follow-up scheduled:',
            FOLLOW_UP_COMPLETED: 'Follow-up: Completed',
            FOLLOW_UP_SCHEDULED_LABEL: 'Follow-up: Scheduled',
            EMAIL_TO_LABEL: 'To:',
            LABEL_FROM: 'From:',
            LABEL_TO: 'To:',
            LABEL_ACTIVITY: 'Activity',
            LABEL_RELATIVE_TIME: 'Relative time',
            LABEL_MESSAGE_SUMMARY: 'Message summary',
            EMAIL_SUBJECT_LABEL: 'Subject:',
            EMAIL_CONTENT_LABEL: 'Content:',
            FOLLOWUP_COMPLETED_CLASS: 'followup-completed',
            FOLLOWUP_SCHEDULED_CLASS: 'followup-scheduled',
            DURATION_MINUTES_SUFFIX: 'm',
            DURATION_HOURS_SUFFIX: 'h',
            DATE_TIME_LOCALE: 'en-US',
            // Record type strings for template comparisons
            RECORD_TYPE_CALL_STRING: 'call',
            RECORD_TYPE_EMAIL_STRING: 'email',
            RECORD_TYPE_IN_PERSON_STRING: 'in_person',
            RECORD_TYPE_VIRTUAL_MEETING_STRING: 'virtual_meeting',
            RECORD_TYPE_MEETING_DISPLAY: 'meeting',
            COMMUNICATION_TYPE_CALL: LABEL_CALL,
            COMMUNICATION_TYPE_EMAIL: LABEL_EMAIL,
            COMMUNICATION_TYPE_WHATSAPP: LABEL_WHATSAPP,
            COMMUNICATION_TYPE_LINKEDIN: LABEL_LINKEDIN,
            COMMUNICATION_TYPE_IN_PERSON: 'In Person',
            COMMUNICATION_TYPE_VIRTUAL_MEETING: LABEL_VIRTUAL_MEETING,
            // Filter tab labels
            FILTER_TAB_ALL: 'All',
            FILTER_TYPE_LINKEDIN: 'linkedin',
            FILTER_TYPE_WHATSAPP: 'whatsapp',
            FILTER_TYPE_IN_PERSON: 'in_person',
            FILTER_TYPE_VIRTUAL_MEETING: 'virtual_meeting',
            // Status CSS classes
            STATUS_CLASS_SUCCESS: 'status-success',
            STATUS_CLASS_ERROR: 'status-error',
            STATUS_CLASS_WARNING: 'status-warning',
            STATUS_CLASS_DEFAULT: 'status-default',
            STATUS_CLASS_EMAIL: 'status-email',
            UPLOAD_DOCUMENT: 'Upload Document',
            SELECT_DOCUMENT_TYPE: 'Select Document Type',
            DOCUMENT_DETAILS_MODAL_TITLE: 'Document Details & Approval History',
            DOCUMENT_INFO_TITLE: 'Document Info',
            TIMELINE_TITLE: 'Timeline',
            INTERNAL_APPROVAL: 'Internal Approvals',
            INTERNAL_APPROVAL_REQUIRED: 'Internal Approval Required?',
            STATUS_LABEL: 'Status',
            UPLOADED_BY_LABEL: 'Uploaded by',
            UPLOADED_ON_LABEL: 'Uploaded on',
            FILE_SIZE_LABEL: 'File size',
            VERSION_LABEL: 'Version',
            NO_DOCUMENTS_UPLOADED: 'No documents uploaded yet',
            VIEW_ALL_VERSIONS: 'View all versions',
            VERSIONS_LABEL: 'versions',
            VIEW_DOCUMENT_PREFIX: 'View ',
            /** Tooltip on document row “review” (eye) action */
            DOCUMENT_ROW_REVIEW_FILE_TOOLTIP: 'Review File',
            /** Tooltip when proposal/contract still needs internal review submission */
            DOCUMENT_ROW_SEND_FOR_APPROVALS_TOOLTIP: 'Send for Approvals',
            /** Compact label next to send icon on document row */
            DOCUMENT_ROW_SEND_FOR_APPROVAL_SHORT: 'Send for Approval',
            /** Label for secondary row action (matches send CTA shape) */
            DOCUMENT_ROW_REVIEW_SHORT: 'Review Document',
            /** Footer hint when proposal/contract needs internal review */
            DOCUMENT_ROW_FOOTER_HINT_SEND: 'Add approvers and send for internal review.',
            /** Footer hint when opening details / timeline */
            DOCUMENT_ROW_FOOTER_HINT_REVIEW: 'Open for file preview, timeline & approvals.',
            DEPARTMENT_COLUMN: 'Department',
            DESIGNATION_COLUMN: 'Designation',
            POC_COLUMN: 'POC',
            STATUS_COLUMN: 'Status',
            ATTACHMENT_COLUMN: 'Attachment',
            ACTIONS_COLUMN: 'Actions',
            ADD_APPROVAL_ROW: '+ Add Approval',
            UPLOAD_REVISED_PROPOSAL: 'Upload Revised Document',
            SUBMIT_APPROVAL: 'Submit',
            REMARKS_LABEL: 'Remarks',
            REMARKS_PLACEHOLDER: 'Enter remarks (required)',
            REMARKS_PLACEHOLDER_COMMENTED: COMMON_NOTES.REMARKS_COMMENTED,
            REMARKS_PLACEHOLDER_REJECTED: COMMON_NOTES.REMARKS_REJECTED,
            SELECT_STATUS: 'Select Status',
            SELECT_DEPARTMENT: 'Select Department',
            SELECT_DESIGNATION: 'Select Designation',
            SELECT_POC: 'Select POC',
            REASON_MODAL_TITLE: 'Reason for Status Change',
            REASON_MODAL_INSTRUCTION:
                'You are changing the approval status to Rejected. Please provide a reason for this change.',
            REASON_LABEL: 'Reason',
            REASON_PLACEHOLDER: 'Enter the reason for this status change...',
            REASON_CHAR_COUNTER: 'characters',
            REASON_REQUIRED_ERROR: 'Reason is required',
            REASON_MAX_LENGTH_ERROR: (maxLength: number): string =>
                `Maximum ${maxLength} characters allowed`,
            REASON_MODAL_WIDTH: '640px',
            REASON_MODAL_MAX_WIDTH: SHARED_CONSTANTS.MAX_WIDTH_90VW,
            APPROVAL_STATUS_LABELS: {
                REJECTED: 'rejected',
                SENT_FOR_APPROVAL: 'pending_approval',
                COMMENTED: 'commented',
                APPROVED: 'approved',
            },
            // Display labels for dropdown (frontend display)
            APPROVAL_STATUS_DISPLAY_LABELS: {
                REJECTED: 'Rejected',
                SENT_FOR_APPROVAL: 'Send for Approval', // All caps for dropdown
                COMMENTED: 'Commented',
                APPROVED: 'Approved',
            },
            LOADING_APPROVALS: 'Loading customer details...',
            NO_APPROVAL_ROWS: 'No approval rows added yet',
            SUBMITTED_BADGE: 'Submitted',
            DELETE_APPROVAL_ARIA_LABEL: 'Delete approval',
            EDIT_APPROVAL_ARIA_LABEL: 'Edit approval',
            REMOVE_FILE_ARIA_LABEL: 'Remove file',
            SAVE_BUTTON: 'Save',
            CANCEL_BUTTON: 'Cancel',
            APPROVAL_PROOF_MODAL_TITLE: 'Approval Proof Required',
            APPROVAL_PROOF_MODAL_INSTRUCTION:
                'Please upload an attachment (screenshot/document) as proof of approval before marking this as "Approved".',
            UPLOAD_PROOF_BUTTON: 'Upload Proof',
            CHOOSE_FILE_BUTTON: 'Choose File',
            DOCUMENT_NAME_MODAL_TITLE: 'Name Your Document',
            DOCUMENT_NAME_MODAL_INSTRUCTION:
                'Please enter a name for this "Other" document type before uploading.',
            DOCUMENT_NAME_LABEL: 'Document Name',
            DOCUMENT_NAME_PLACEHOLDER: 'e.g., Compliance Certificate',
            DOCUMENT_NAME_REQUIRED_ERROR: 'Document name is required',
            DOCUMENT_NAME_MAX_LENGTH_ERROR: (maxLength: number): string =>
                `Document name must be ${maxLength} characters or less`,
            DOCUMENT_NAME_MODAL_WIDTH: '500px',
            DOCUMENT_NAME_MODAL_MAX_WIDTH: SHARED_CONSTANTS.MAX_WIDTH_90VW,
            DOCUMENT_NAME_MAX_LENGTH: 255,
            DOCUMENT_FALLBACKS: {
                NAME: 'Untitled Document',
                TYPE: 'Document',
            },
            DATE_FORMAT: {
                PATTERN: NAVIGATOR_DATETIME_DISPLAY_24H,
                LOCALE: SHARED_CONSTANTS.LOCALE_EN_GB,
            },
            FORM_DATA_FIELDS: {
                METADATA: 'metadata',
                FILE: SHARED_CONSTANTS.FORM_FIELD_FILE,
            },
            S3_KEY_TEMPLATE: 'leads/{leadId}/documents/{documentId}/attachments/{fileName}',
        },
        BUTTONS: {
            SAVE_CALL: 'Save Call',
            SAVE_MESSAGE: 'Save Message',
            SAVE_EMAIL: 'Save Email',
            SAVE_IN_PERSON: 'Save Meeting',
            SAVE_VIRTUAL_MEETING: 'Save virtual meeting',
        },
        STAGE_ACTION_REQUIRED: {
            STAGE_1: {
                CHECKLIST: {
                    FILL_DEAL_INFO_LABEL: 'Fill Deal Information for S1: Deal Qualification',
                    ADD_NOTE_FOR_THIS_STAGE_S1:
                        'Add at least one note in this stage to complete deal information.',
                },
                STEP_1: {
                    TITLE: 'Discuss Requirements',
                    SUBTEXT: "Discuss the customer's needs and record all related communication.",
                    BUTTONS: {
                        RECORD_COMMUNICATION: 'Record Communication',
                        REQUIREMENTS_CONFIRMED: 'Requirements Confirmed',
                    },
                    CONFIRMATION_CARD: {
                        TITLE: 'Requirements confirmed',
                        COMMUNICATION_ONLY_TITLE: 'Communication added successfully',
                        LAST_RECORDED_COMMUNICATION_PREFIX: 'Last recorded communication:',
                        REQUIREMENTS_CONFIRMATION_DATE_PREFIX: 'Requirements confirmation date:',
                    },
                },
                STEP_2: {
                    TITLE: 'Customer Qualification',
                    SUBTEXT: 'Determine whether the customer is qualified for further engagement.',
                    BUTTONS: {
                        YES: LABEL_YES,
                        NO: LABEL_NO,
                    },
                    OPTIONAL_BUDGET_QUESTION:
                        '(Optional) Does the customer have an annual budget of at least ₹25,00,000?',
                    OPTIONAL_BUDGET_BUTTONS: {
                        YES: LABEL_YES,
                        NO: LABEL_NO,
                        DONT_KNOW: "Don't know",
                    },
                },
                STEP_3: {
                    TITLE: "Record Customer's Interest",
                    SUBTEXT: "Record the customer's interest level to decide the next step.",
                    BUTTONS: {
                        INTERESTED_MOVE_TO_S2: 'Interested → Move to S2: Proposal',
                        DEAL_DROPPED: 'Deal Lost',
                    },
                },
                DIALOGS: {
                    COMMON: {
                        WIDTH: '400px',
                        BACKDROP_CLASS: 'common-dialog-backdrop',
                    },
                    REQUIREMENTS_CONFIRMATION: {
                        TITLE: 'Requirements Confirmation',
                        MESSAGE:
                            "Are you sure you understand the client requirements?\n\nThis action will mark that you have discussed and understood the client's requirements.",
                        CONFIRM_TEXT: 'Confirm',
                        CANCEL_TEXT: LABEL_CLOSE,
                        PANEL_CLASS: 'requirements-confirmation-dialog',
                    },
                    DEAL_DROPPED_CONFIRMATION: {
                        TITLE: 'Deal Lost',
                        MESSAGE:
                            'Are you sure you want to mark this deal as lost? This cannot be undone.',
                        CONFIRM_TEXT: 'Mark as Lost',
                        CANCEL_TEXT: LABEL_CLOSE,
                        PANEL_CLASS: 'deal-dropped-confirmation-dialog',
                        WIDTH: '500px',
                        NOTE_LABEL: LABEL_NOTE,
                        NOTE_PLACEHOLDER: 'Enter note for deal loss',
                    },
                },
                SNACKBAR: {
                    AT_LEAST_ONE_NOTE_ERROR:
                        'Add at least one note in this stage before confirming requirements.',
                    REQUIRED_FIELDS_ERROR:
                        'Please fill all required fields for this stage before confirming requirements.',
                },
                PLACEHOLDERS: {
                    LAST_COMMUNICATION_TEXT: 'Meeting (Jan 16, 2026)',
                },
            },
            STAGE_2: {
                CHECKLIST: {
                    FILL_DEAL_INFO_S2: 'Fill Deal Information for S2: Proposal',
                    ADD_NOTE_FOR_THIS_STAGE_S2:
                        'Add at least one note in this stage to complete deal information.',
                    UPLOAD_DOCUMENTS: 'Upload proposal document(s)',
                    SEND_FOR_INTERNAL_REVIEW: 'Send proposal for internal review',
                    GET_INTERNAL_APPROVALS: 'Get internal approvals on the proposal document(s)',
                },
                PROPOSAL_SENT_QUESTION: 'Proposal sent to client?',
                SENT_TO_CLIENT_BUTTON: 'Proposal sent to client',
                SEND_EMAIL_TO_CLIENT_BUTTON: 'Send email to client (optional)',
                SNACKBAR: {
                    FAILED_TO_MOVE_TO_STAGE_3: 'Failed to move deal to Stage 3',
                },
                DIALOGS: {
                    COMMON: {
                        WIDTH: '400px',
                        BACKDROP_CLASS: 'common-dialog-backdrop',
                    },
                    SENT_TO_CLIENT_CONFIRMATION: {
                        TITLE: 'Proposal sent to client?',
                        MESSAGE:
                            'Confirm that the proposal has been shared with the client.\n\nThe deal will move to Stage 3 without sending an email from Navigator.',
                        CONFIRM_TEXT: 'Confirm',
                        CANCEL_TEXT: LABEL_CLOSE,
                    },
                },
            },
            STAGE_3: {
                STEP_1: {
                    TITLE: 'Client response',
                    BUTTONS: {
                        PROPOSAL_ACCEPTED: 'Proposal accepted',
                        CLIENT_REQUESTED_EDITS: 'Client requested edits',
                    },
                },
                CHECKLIST: {
                    UPLOAD_DOCUMENTS: 'Upload proposal document(s)',
                    SEND_FOR_INTERNAL_REVIEW: 'Send proposal for internal review',
                    GET_INTERNAL_APPROVALS: 'Get internal approvals on the proposal document(s)',
                },
                PROPOSAL_ACCEPTED_QUESTION: 'Proposal ready to accept?',
                PROPOSAL_SENT_TO_CLIENT_QUESTION: 'Proposal sent to client?',
                PROPOSAL_SENT_TO_CLIENT_BUTTON: 'Proposal sent to client',
                SEND_EMAIL_TO_CLIENT_BUTTON: 'Send email to client (optional)',
                SNACKBAR: {
                    FAILED_TO_MARK_SENT_TO_CLIENT: 'Failed to mark the proposal as sent to client',
                    AT_LEAST_ONE_NOTE_S3_ERROR:
                        'Add at least one note in this stage before accepting the proposal.',
                    CHECKLIST_INCOMPLETE_ERROR:
                        'Please complete all checklist items before accepting the proposal.',
                    PROPOSAL_SENT_TO_CLIENT_SUCCESS: 'Proposal sent to client successfully.',
                },
                DIALOGS: {
                    COMMON: {
                        WIDTH: '520px',
                        MAX_WIDTH: '90vw',
                        BACKDROP_CLASS: 'common-dialog-backdrop',
                    },
                    CLIENT_REQUESTED_EDITS: {
                        TITLE: 'Client Requested Edits',
                    },
                    SENT_TO_CLIENT_CONFIRMATION: {
                        TITLE: 'Proposal sent to client?',
                        MESSAGE:
                            'Confirm that the revised proposal has been shared with the client.\n\nNo email is sent from Navigator.',
                        CONFIRM_TEXT: 'Confirm',
                        CANCEL_TEXT: LABEL_CLOSE,
                        WIDTH: '400px',
                    },
                    REQUEST_EDITS_UPLOAD: {
                        TITLE: 'Upload Revised Document',
                        DESCRIPTION:
                            'The client has requested edits. Please upload the revised proposal document. After upload, you will need to complete the checklist (Upload Documents, Send for Review, Get Approvals) before accepting the proposal.',
                        REJECTION_DESCRIPTION:
                            'This proposal has been rejected. Please upload the revised proposal document.',
                        UPLOAD_BUTTON: 'Upload Document',
                        SUBMIT_BUTTON: 'Upload & Continue',
                        DRAG_DROP_TEXT: 'Drag & drop your file here or click to browse',
                        CLICK_TO_BROWSE_TEXT: 'or click to browse',
                        UPLOADING_TEXT: 'Uploading...',
                        WIDTH: '600px',
                        MAX_WIDTH: '600px',
                    },
                    PROPOSAL_ACCEPTED: {
                        TITLE: 'Proposal Accepted',
                        MESSAGE:
                            'Please add a short note about the client accepting the proposal (key terms agreed, next steps).',
                        CONFIRM_TEXT: LABEL_SAVE,
                        CANCEL_TEXT: LABEL_CANCEL,
                        NOTE_LABEL: LABEL_NOTE,
                        NOTE_PLACEHOLDER: 'Add details about proposal acceptance...',
                        WIDTH: '448px',
                        MAX_WIDTH: '448px',
                    },
                },
            },
            STAGE_4: {
                CHECKLIST: {
                    FILL_DEAL_INFO_S4_PREFIX: 'Fill Deal Information for S4:',
                    UPLOAD_DOCUMENTS: 'Upload contract document(s)',
                    SEND_FOR_INTERNAL_REVIEW: 'Send contract for internal review',
                    GET_INTERNAL_APPROVALS: 'Get internal approvals on the contract document(s)',
                },
                CONTRACT_SENT_QUESTION: 'Contract Sent to Client',
                CONTRACT_SENT_SUCCESSFULLY: 'Contract Sent to Client Successfully',
                SENT_TO_CLIENT_BUTTON: 'Send to Client',
                CUSTOMER_RESPONSE: "Customer's Response",
                BUTTONS: {
                    CONTRACT_ACCEPTED: 'Contract Accepted',
                    REQUESTED_EDITS: 'Requested Edits',
                },
                ERROR_DEAL_INFO_INCOMPLETE: (stageName: string) =>
                    `Complete the deal information for ${stageName} before sending the contract to the client.`,
                SNACKBAR: {
                    CHECKLIST_INCOMPLETE_ERROR:
                        'Please complete all checklist items before accepting the contract.',
                },
                DIALOGS: {
                    COMMON: {
                        BACKDROP_CLASS: 'common-dialog-backdrop',
                    },
                    SEND_TO_CLIENT_CONFIRMATION: {
                        TITLE: 'Send Contract to Client',
                        MESSAGE:
                            'This will send the contract to the client. Do you want to continue?',
                        CONFIRM_TEXT: 'Confirm',
                        CANCEL_TEXT: LABEL_CANCEL,
                    },
                    REQUEST_EDITS_UPLOAD: {
                        TITLE: 'Upload Revised Document',
                        DESCRIPTION:
                            'The client has requested edits. Please upload the revised contract document. After upload, you will need to complete the checklist (Upload Documents, Send for Review, Get Approvals) before accepting the contract.',
                        REJECTION_DESCRIPTION:
                            'This contract has been rejected. Please upload the revised contract document.',
                        UPLOAD_BUTTON: 'Upload Document',
                        SUBMIT_BUTTON: 'Upload & Continue',
                        DRAG_DROP_TEXT: 'Drag & drop your file here or click to browse',
                        CLICK_TO_BROWSE_TEXT: 'or click to browse',
                        UPLOADING_TEXT: 'Uploading...',
                        WIDTH: '600px',
                        MAX_WIDTH: '600px',
                    },
                },
            },
        },
        PROPOSAL_ACCEPTED_MODAL: {
            TITLE: 'Confirm Proposal Acceptance',
            COMMUNICATION_MEDIUM_LABEL: LABEL_COMMUNICATION_MEDIUM,
            ESTIMATED_DEAL_VALUE_LABEL: 'Estimated Deal Value (₹)',
            PLACEHOLDER_AMOUNT: 'Enter amount',
            RUPEES_ONLY: 'Rupees only',
            ERROR_AMOUNT_REQUIRED: 'Amount is required',
            ERROR_AMOUNT_NEGATIVE: 'Amount cannot be negative',
            ERROR_AMOUNT_MAX: (max: number) =>
                `Amount must not exceed ₹${max.toLocaleString('en-IN')}`,
            CANCEL: LABEL_CANCEL,
            CONFIRM_ACCEPTANCE: 'Confirm Acceptance',
            DEFAULT_COMMUNICATION_MEDIUM: 'call',
            COMMUNICATION_OPTIONS: [
                { value: 'call', label: LABEL_CALL },
                { value: 'email', label: LABEL_EMAIL },
                { value: 'whatsapp', label: LABEL_WHATSAPP },
                { value: 'in_person', label: LABEL_IN_PERSON },
            ] as readonly { value: string; label: string }[],
        },
        EXPANDABLE_STAGE_NAMES: {
            1: 'Deal Qualification',
            2: 'Proposal',
            3: 'Engagement',
            4: 'Project Confirmation',
            5: 'Deal Won',
        },
        TOOLTIPS: {
            SCHEDULE_VISIT_COMING_SOON: 'This feature will be available soon',
            COMING_SOON: 'Coming Soon',
            DEAL_INFORMATION: 'Information about the deal and its stages',
            INTERESTED_PRODUCTS: 'Products that the customer is interested in',
            DEAL_STATUS: 'Deal Status',
        },
        FORM_LABELS: {
            STAGE_1: {
                DEAL_ID: 'Deal ID',
                DEAL_NAME: 'Deal Name',
                DEAL_OWNER: 'Deal Owner',
                FINANCIAL_YEAR: 'Financial Year',
                ENGAGEMENT_TYPE: 'Engagement Type',
                OPPORTUNITY_TYPE: 'Opportunity Type',
                INITIAL_CONTACT_DATE: 'Initial Contact Date',
                INITIAL_CONTACT_CHANNEL: 'Initial Contact Channel',
                LAST_INTERACTION_DATE: 'Last Interaction Date',
                LAST_INTERACTION_CHANNEL: 'Last Interaction Channel',
                NO_PRODUCTS_AVAILABLE: 'No products available',
                INTERESTED_PRODUCT: 'Interested Product:',
                INTERESTED_PRODUCTS: 'Interested Products:',
            },
            STAGE_2: {
                ESTIMATE_BUDGET: 'Estimate Budget',
                PROPOSED_PRODUCTS: 'Proposed Products',
                BUDGET_SHARED_BY_CUSTOMER: 'Budget Shared by Customer?',
                CUSTOMER_SHARED_PROJECT_LOCATIONS: 'Has the Customer Shared Project Locations?',
                CUSTOMER_PROVIDED_REVIEW_DATE: 'Has Customer Provided Review Date?',
                REVIEW_DATE_EXPECTED: 'Review Date (Expected)',
            },
            STAGE_3: {
                COMMUNICATION_MEDIUM: LABEL_COMMUNICATION_MEDIUM,
                ESTIMATED_AMOUNT: 'Estimated Amount',
                PROPOSAL_ACCEPTED_DATE: 'Proposal Accepted Date',
            },
            STAGE_4: {
                ORDER_DATES: 'Order Dates',
                START_DATE: 'Start Date',
                END_DATE: 'End Date',
                PAYMENT_MILESTONES: 'Payment Milestones',
                ADD_MILESTONE_BUTTON: 'Add Milestone',
                EDIT_MILESTONE_BUTTON: 'Edit Payment Milestones',
                MILESTONE_SUMMARY_DELIVERABLE: 'Deliverable Name',
                MILESTONE_SUMMARY_TOTAL_AMOUNT: 'Total Amount',
                MILESTONE_SUMMARY_TOTAL_AMOUNT_INCL_GST: 'Total Amount (incl. GST)',
                MILESTONE_SUMMARY_PERCENT: '% of Contract Value',
                MILESTONE_SUMMARY_PAYMENT_DATE: 'Payment Date',
                MILESTONE_GST_TAG: 'GST: 18% applied',
                REVENUE_BREAKDOWN_BY_FY: 'Revenue Breakdown by Fiscal Year',
                FISCAL_YEAR: 'Fiscal Year',
                REVENUE: 'Revenue',
                REVENUE_INCL_GST: 'Revenue (incl. GST)',
                FISCAL_YEAR_TOTAL_ROW: 'Total',
                FISCAL_YEAR_SUBTOTAL_ROW: 'Subtotal',
                GRAND_TOTAL: 'GRAND TOTAL',
                TOTAL_CONTRACT_VALUE: 'Total Contract Value',
                TOTAL_EXCL_GST: 'Total (excl. GST)',
                TOTAL_INCL_GST: 'Total (incl. GST)',
                ACCOUNT_MANAGER: 'Account Manager',
            },
            STAGE_5: {
                CONTRACT_VALUE: 'Contract Value',
                ACCOUNT_MANAGER: 'Account Manager',
                START_DATE: 'Start Date',
                END_DATE: 'End Date',
                PAYMENT_MILESTONES: 'Payment Milestones',
            },
        },
        FORM_ERROR_MESSAGES: {
            STAGE_1: {
                FINANCIAL_YEAR_REQUIRED: 'Financial Year is required',
                ENGAGEMENT_TYPE_REQUIRED: 'Engagement Type is required',
            },
            STAGE_2: {
                ESTIMATE_BUDGET_REQUIRED: 'Estimate Budget is required',
                PROPOSED_PRODUCTS_REQUIRED: 'Proposed Products is required',
                BUDGET_SHARED_REQUIRED: 'Budget Shared by Customer is required',
                PROJECT_LOCATIONS_REQUIRED: 'Has the Customer Shared Project Locations is required',
                REVIEW_DATE_QUESTION_REQUIRED: 'Has Customer Provided Review Date is required',
                REVIEW_DATE_REQUIRED: 'Review Date is required',
                REVIEW_DATE_MIN_DATE: 'Review Date must be today or a future date',
            },
            STAGE_4: {
                START_DATE_REQUIRED: 'Start Date is required',
                START_DATE_INVALID: 'Start date must be a valid date',
                START_DATE_MIN: 'Start date must be today or a future date',
                END_DATE_REQUIRED: 'End Date is required',
                END_DATE_INVALID: 'End date must be a valid date',
                END_DATE_MIN: 'End date must be on or after Start date',
                ACCOUNT_MANAGER_REQUIRED: 'Account Manager is required',
                PAYMENT_MILESTONES_REQUIRED:
                    'Payment Milestones are required. Please add at least one payment milestone before saving.',
                MILESTONE_DATES_OUTSIDE_RANGE:
                    'One or more payment milestone dates fall outside the contract period. Please update the dates via Edit Payment Milestones so all dates are between the Start Date and End Date.',
            },
        },
        ICONS: {
            SWAP_HORIZ: 'swap_horiz',
            REASSIGN: 'user_plus',
            EVENT: 'event',
            MESSAGE: 'message',
            RECORD_COMMUNICATION: 'message_square',
            ACCOUNT_CIRCLE: 'account_circle',
            PHONE: 'phone',
            MAIL: 'mail',
            WORK: 'work',
            PEOPLE: 'people',
            ACCESS_TIME: 'access_time',
            EDIT: 'edit',
        },
        STAGE_NOTE_TYPE_LABELS,
        STAGE_NOTES: {
            LABELS: {
                TITLE: 'Note Types',
                NOTE_LABEL: 'Note',
                PLACEHOLDER: COMMON_NOTES.NOTE_STAGE,
                STAGE_ACTIVITY_TITLE_LABELS: {
                    SINGULAR: 'Stage Note',
                    PLURAL: 'Stage Notes',
                } as const,
                STAGE_ACTIVITY_SUBTITLE: 'All communications related to this stage.',
                LOAD_MORE: 'Load More',
                VIEW_LESS: 'View less',
                NO_NOTES_YET_TITLE: 'No notes yet',
                NO_NOTES_YET: 'Add a note above to record activity for this stage.',
                ADD_NOTE: 'Add note',
                ADDED_BY_YOU: 'You',
                REFRESH_TOOLTIP: 'Refresh notes',
                REFRESH_ARIA_LABEL: 'Refresh notes',
                NOTE_TYPE_GENERAL: STAGE_NOTE_TYPE_LABELS.GENERAL,
                NOTE_TYPE_NEXT_STEPS: STAGE_NOTE_TYPE_LABELS.NEXT_STEPS,
                NOTE_TYPE_RECOMMENDATION: STAGE_NOTE_TYPE_LABELS.RECOMMENDATION,
            },
            INITIAL_VISIBLE_COUNT: 3,
            SKELETON_ITEM_COUNT: 3,
            TEXTAREA_ROWS: 4,
            MAX_NOTE_LENGTH: 2500,
            VIEW_MORE_STEP: 5,
            COMMENTS_PAGE_LIMIT: 100,
            PAGINATION: {
                DEFAULT_NEXT_PAGE: 1,
                DEFAULT_TOTAL_PAGES: 1,
            },
            ERROR_MESSAGES: {
                NOTE_TYPE_REQUIRED: 'Note type is required',
                NOTE_CONTENT_REQUIRED:
                    'Please add some details in the note before clicking "Add note".',
            },
            TYPES: [
                {
                    value: EStageNoteType.GENERAL,
                    label: STAGE_NOTE_TYPE_LABELS.GENERAL,
                },
                {
                    value: EStageNoteType.RECOMMENDATION,
                    label: STAGE_NOTE_TYPE_LABELS.RECOMMENDATION,
                },
                {
                    value: EStageNoteType.NEXT_STEPS,
                    label: STAGE_NOTE_TYPE_LABELS.NEXT_STEPS,
                },
                {
                    value: EStageNoteType.MISCELLANEOUS,
                    label: STAGE_NOTE_TYPE_LABELS.MISCELLANEOUS,
                },
            ] as readonly IStageNoteTypeOption[],
        },
        COMMUNICATION_TYPE_OPTIONS: [
            {
                value: ERecordCommunicationType.CALL,
                label: LABEL_CALL,
                icon: 'phone',
            },
            {
                value: ERecordCommunicationType.WHATSAPP,
                label: LABEL_WHATSAPP,
                icon: 'message',
            },
            {
                value: ERecordCommunicationType.EMAIL,
                label: LABEL_EMAIL,
                icon: 'mail',
            },
            {
                value: ERecordCommunicationType.LINKEDIN,
                label: LABEL_LINKEDIN,
                icon: 'work',
            },
            {
                value: ERecordCommunicationType.IN_PERSON,
                label: LABEL_IN_PERSON,
                icon: 'people',
            },
            {
                value: ERecordCommunicationType.VIRTUAL_MEETING,
                label: LABEL_VIRTUAL_MEETING,
                icon: 'monitor',
            },
        ] as ICommunicationTypeOption[],
        COMMON: {
            PLACEHOLDERS: {
                ...RECORD_COMMUNICATION_SHARED_PLACEHOLDERS,
            },
            ERROR_MESSAGES: {
                CONTACT_PERSON_REQUIRED: 'Contact person is required',
            },
            ERROR_SUMMARY: {
                TITLE_SINGULAR: 'Please fix the following error:',
                TITLE_PLURAL: 'Please fix the following errors:',
            },
        },
        CALL_FORM: {
            LABELS: {
                CALL_DATE: 'Call Date',
                CALL_TIME: 'Call Time',
                CALL_STATUS: 'Call Status',
                CALL_DURATION: 'Call Duration (minutes)',
                CONTACT_PERSON: 'Contact Person',
                CALL_NOTES: 'Call Notes',
                ADD_NEW_CONTACT: 'Add New Contact',
            },
            PLACEHOLDERS: {
                SELECT_DATE: RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_DATE,
                TIME_INPUT: 'HH:MM AM/PM',
                SELECT_CALL_STATUS: 'Select call status',
                ENTER_DURATION: 'Enter duration',
                SELECT_CONTACT_PERSON:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_CONTACT_PERSON,
                NO_CONTACTS_AVAILABLE:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.NO_CONTACTS_AVAILABLE,
                ENTER_CALL_NOTES: COMMON_NOTES.COMMUNICATION_ALL,
            },
            ARIA_LABELS: {
                ADD_NEW_CONTACT: 'Add new contact',
            },
            ICONS: {
                SCHEDULE: 'schedule',
                PERSON_ADD: 'person_add',
            },
            VALUES: {
                MAX_NOTE_LENGTH: COMMUNICATION_FORM_VALIDATION_LIMITS.NOTE_MAX_LENGTH,
                ...COMMON_COMMUNICATION_FORM_VALUES,
                MIN_DURATION: COMMUNICATION_FORM_VALIDATION_LIMITS.DURATION_MIN_MINUTES,
                MAX_DURATION: COMMUNICATION_FORM_VALIDATION_LIMITS.DURATION_MAX_MINUTES,
                COMMUNICATION_TYPE_CALL: 'call',
            },
            ERROR_MESSAGES: {
                ...COMMON_COMMUNICATION_FORM_ERROR_MESSAGES,
                DURATION_REQUIRED_WHEN_CONNECTED:
                    'is required when call status is "Call Connected"',
            },
        },
        MESSAGE_FORM: {
            LABELS: {
                CONNECTED_DATE: 'Connected Date',
                CONTACT_PERSON: 'Contact Person',
                MESSAGE_SUMMARY: 'Message Summary',
                WHATSAPP_MESSAGE_SUMMARY: 'WhatsApp Message Summary',
                LINKEDIN_MESSAGE_SUMMARY: 'LinkedIn Message Summary',
                ADD_NEW_CONTACT: 'Add New Contact',
            },
            PLACEHOLDERS: {
                SELECT_DATE: RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_DATE,
                SELECT_CONTACT_PERSON:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_CONTACT_PERSON,
                NO_CONTACTS_AVAILABLE:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.NO_CONTACTS_AVAILABLE,
                ENTER_MESSAGE_SUMMARY: COMMON_NOTES.COMMUNICATION_ALL,
                ENTER_WHATSAPP_MESSAGE_SUMMARY: COMMON_NOTES.COMMUNICATION_ALL,
                ENTER_LINKEDIN_MESSAGE_SUMMARY: COMMON_NOTES.COMMUNICATION_ALL,
            },
            ARIA_LABELS: {
                ADD_NEW_CONTACT: 'Add new contact',
            },
            ICONS: {
                PERSON_ADD: 'person_add',
            },
            VALUES: {
                MAX_MESSAGE_LENGTH: COMMUNICATION_FORM_VALIDATION_LIMITS.MESSAGE_SUMMARY_MAX_LENGTH,
                ...COMMON_COMMUNICATION_FORM_VALUES,
            },
            ERROR_MESSAGES: {
                ...COMMON_COMMUNICATION_FORM_ERROR_MESSAGES,
                SELECT_AT_LEAST_ONE: 'is required. Please select at least one contact.',
                DURATION_REQUIRED_WHEN_CONNECTED:
                    'is required when call status is "Call Connected"',
            },
        },
        IN_PERSON_FORM: {
            LABELS: {
                DATE: 'Date',
                TIME: 'Time',
                CONTACT_PERSON: 'Contact Person',
                DISCUSSION_NOTE: 'Discussion Note',
                ADD_NEW_CONTACT: 'Add New Contact',
            },
            PLACEHOLDERS: {
                SELECT_DATE: RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_DATE,
                TIME_INPUT: 'HH:MM AM/PM',
                SELECT_CONTACT_PERSON:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_CONTACT_PERSON,
                NO_CONTACTS_AVAILABLE:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.NO_CONTACTS_AVAILABLE,
                ENTER_DISCUSSION_NOTE: COMMON_NOTES.COMMUNICATION_ALL,
            },
            ARIA_LABELS: {
                ADD_NEW_CONTACT: 'Add new contact',
            },
            ICONS: {
                SCHEDULE: 'schedule',
                PERSON_ADD: 'person_add',
            },
            VALUES: {
                MAX_NOTE_LENGTH: COMMUNICATION_FORM_VALIDATION_LIMITS.NOTE_MAX_LENGTH,
                ...COMMON_COMMUNICATION_FORM_VALUES,
            },
            ERROR_MESSAGES: {
                ...COMMON_COMMUNICATION_FORM_ERROR_MESSAGES,
            },
        },
        VIRTUAL_MEETING_FORM: {
            LABELS: {
                MEETING_PLATFORM: 'Platform name',
                CUSTOM_PLATFORM_NAME: 'Type platform name',
                DATE: 'Date',
                TIME: 'Time',
                CONTACT_PERSON: 'Contact Person',
                DISCUSSION_NOTE: 'Discussion Note',
                ADD_NEW_CONTACT: 'Add New Contact',
            },
            PLACEHOLDERS: {
                SELECT_DATE: RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_DATE,
                SELECT_MEETING_PLATFORM: 'Select platform name',
                TIME_INPUT: 'HH:MM AM/PM',
                SELECT_CONTACT_PERSON:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_CONTACT_PERSON,
                NO_CONTACTS_AVAILABLE:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.NO_CONTACTS_AVAILABLE,
                ENTER_DISCUSSION_NOTE: COMMON_NOTES.COMMUNICATION_ALL,
                ENTER_CUSTOM_PLATFORM: 'Enter platform name',
            },
            ARIA_LABELS: {
                ADD_NEW_CONTACT: 'Add new contact',
            },
            ICONS: {
                SCHEDULE: 'schedule',
                PERSON_ADD: 'person_add',
            },
            VALUES: {
                MAX_NOTE_LENGTH: COMMUNICATION_FORM_VALIDATION_LIMITS.NOTE_MAX_LENGTH,
                CUSTOM_PLATFORM_MAX_LENGTH: 30,
                ...COMMON_COMMUNICATION_FORM_VALUES,
            },
            MEETING_PLATFORM_OPTIONS: [
                { value: 'zoom', label: 'Zoom' },
                { value: 'google_meet', label: 'Google Meet' },
                { value: 'microsoft_teams', label: 'Microsoft Teams' },
                { value: 'other', label: 'Other' },
            ] as const,
            ERROR_MESSAGES: {
                ...COMMON_COMMUNICATION_FORM_ERROR_MESSAGES,
                MEETING_PLATFORM_REQUIRED: 'Platform name is required',
                CUSTOM_PLATFORM_REQUIRED: 'Type platform name is required when Other is selected',
                CUSTOM_PLATFORM_PATTERN:
                    'Use only letters, numbers, and spaces (max 30 characters)',
            },
        },
        EMAIL_FORM: {
            LABELS: {
                DATE: 'Date',
                TIME: 'Time',
                TO: 'To',
                SUBJECT: 'Subject',
                EMAIL_CONTENT: 'Email Content',
                ADDITIONAL_NOTE: 'Additional Note',
                ADD_NEW_CONTACT: 'Add New Contact',
            },
            PLACEHOLDERS: {
                SELECT_DATE: RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_DATE,
                TIME_INPUT: 'HH:MM AM/PM',
                SELECT_CONTACT_PERSON:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_CONTACT_PERSON,
                NO_CONTACTS_AVAILABLE:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.NO_CONTACTS_AVAILABLE,
                ENTER_SUBJECT: 'Enter subject',
                ENTER_EMAIL_CONTENT: COMMON_NOTES.COMMUNICATION_ALL,
                ENTER_ADDITIONAL_NOTE: 'Enter additional note...',
            },
            ARIA_LABELS: {
                ADD_NEW_CONTACT: 'Add new contact',
            },
            ICONS: {
                SCHEDULE: 'schedule',
                PERSON_ADD: 'person_add',
            },
            VALUES: {
                MAX_SUBJECT_LENGTH: 200,
                MAX_CONTENT_LENGTH: 1500,
                MAX_NOTE_LENGTH:
                    COMMUNICATION_FORM_VALIDATION_LIMITS.EMAIL_ADDITIONAL_NOTE_MAX_LENGTH,
                ...COMMON_COMMUNICATION_FORM_VALUES,
            },
            ERROR_MESSAGES: {
                ...COMMON_COMMUNICATION_FORM_ERROR_MESSAGES,
                SELECT_AT_LEAST_ONE: 'is required. Please select at least one contact.',
            },
        },
    },
    DIALOG: {
        WIDTH: '448px',
        MAX_WIDTH: '448px',
        REASSIGN_DEAL_MODAL_WIDTH: '520px',
        REASSIGN_DEAL_MODAL_MAX_WIDTH: '520px',
        CREATE_DEAL_MODAL_WIDTH: '460px',
        CREATE_DEAL_MODAL_MAX_WIDTH: '460px',
        RECORD_COMMUNICATION_MODAL_WIDTH: '540px',
        RECORD_COMMUNICATION_MODAL_MAX_WIDTH: '540px',
        DEAL_FORM_MODAL_WIDTH: '90%',
        DEAL_FORM_MODAL_MAX_WIDTH: SHARED_CONSTANTS.WIDTH_600PX,
        VIEW_ALL_CONTACTS_MODAL_MAX_WIDTH: '900px',
        DOCUMENT_DETAILS_MODAL_WIDTH: '90vw',
        DOCUMENT_DETAILS_MODAL_MAX_WIDTH: '1800px',
        BACKDROP_CLASS: 'common-dialog-backdrop',
        DELAY_BEFORE_OPEN: 100,
        DELETE_CONFIRMATION_WIDTH: '448px',
        CONGRATULATIONS: {
            TITLE: 'Congratulations',
            CLOSE_BUTTON: 'OK',
            MESSAGES: {
                MOVE_TO_S2:
                    'The deal has been moved to Stage 2: Proposal. You can now upload documents and continue.',
                MOVE_TO_S3:
                    'The deal has been moved to Stage 3: Proposal Accepted. You can now record the client response.',
                SENT_TO_CLIENT: 'The proposal has been sent to the client successfully.',
                CONTRACT_SENT_TO_CLIENT: 'The contract has been sent to the client successfully.',
                PROPOSAL_ACCEPTED:
                    'The proposal has been accepted. The deal has moved to the next stage.',
                CONTRACT_ACCEPTED:
                    'The contract has been accepted. The deal has moved to the final stage.',
            },
        },
        ADD_MILESTONE: {
            TITLE: 'Payment Milestones',
            TITLE_INCL_GST: 'Payment Milestones (incl. GST)',
            CLOSE_BUTTON: LABEL_CLOSE,
            CANCEL: LABEL_CANCEL,
            SAVE: 'Save Milestones',
            CONTRACT_VALUE_LABEL: 'Contract value',
            CONTRACT_VALUE_LABEL_EXCL_GST: 'Contract value (exc. GST)',
            CONTRACT_VALUE_LABEL_INCL_GST: 'Contract value (incl. GST)',
            CONTRACT_VALUE_PLACEHOLDER: 'Enter contract value',
            GST_LABEL_INCLUSIVE: 'Inclusive GST',
            GST_LABEL_EXCLUSIVE: 'Exclusive GST',
            GST_TAG: '18% GST applicable',
            GST_AMOUNT_LABEL: 'GST amount',
            DELIVERABLE_NAME: 'Deliverable Name',
            DELIVERABLE_NAME_PLACEHOLDER: 'Milestone 1',
            PERCENT_OF_CONTRACT: '% of Contract Value',
            PERCENT_PLACEHOLDER: '25',
            AMOUNT_INR: 'Amount in INR',
            AMOUNT_PLACEHOLDER: '50000',
            AMOUNT_EXC_GST: 'Amount in INR (exc. GST)',
            AMOUNT_INCL_GST: 'Amount in INR (incl. GST)',
            GST_EXC_LABEL: '(exc. GST)',
            GST_INCL_LABEL: '(incl. GST)',
            ESTIMATED_PAYMENT_DATE: 'Estimated Payment Date',
            ESTIMATED_PAYMENT_DATE_PLACEHOLDER:
                RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.SELECT_DATE,
            SR_NO: 'Sr. No.',
            ACTIONS: 'Actions',
            DELETE_ROW_ARIA: 'Remove milestone row',
            ADD_ROW_BUTTON: '+ Add Milestone',
            SYNC_EQUALLY_BUTTON: 'Distribute equally',
            INFO_HEADING: '',
            /** Shown in modal when milestone table is visible (see add-milestone template vs `HINT_ENTER_CONTRACT_VALUE`). */
            INFO_DESCRIPTION: MILESTONE_CONTRACT_HELPER,
            HINT_ENTER_CONTRACT_VALUE: MILESTONE_CONTRACT_HELPER,
            HINT_MILESTONES_INFO:
                'Add milestones. Percent and amount sync automatically; sum of amounts must equal Contract Value and total of percentages must equal 100%.',
            ERROR_CONTRACT_VALUE_REQUIRED: 'Contract Value is required',
            ERROR_CONTRACT_VALUE_POSITIVE: 'Contract Value must be a positive number',
            ERROR_DATES_REQUIRED:
                'Please select Start Date and End Date before adding payment milestones.',
            ERROR_SUM_MISMATCH: 'Sum of milestone amounts must equal Contract Value.',
            ERROR_PERCENT_TOTAL_MISMATCH: 'Total of milestone percentages must equal 100%.',
            ERROR_ROW_INCOMPLETE:
                'Please fill all fields in the current row before adding another.',
            ERROR_ROW_COUNT: 'Between 1 and 100 milestones are required.',
            ERROR_DELIVERABLE_REQUIRED: 'Deliverable name is required',
            ERROR_PERCENT_REQUIRED: 'Percent is required',
            ERROR_PERCENT_GT_ZERO: 'Percentage of contract value must be greater than 0.',
            ERROR_PERCENT_MAX: 'Each row cannot exceed 100% of contract value.',
            ERROR_AMOUNT_REQUIRED: 'Amount is required',
            ERROR_DUPLICATE_MILESTONE_NAME: 'Milestone name must be unique.',
            ERROR_DATE_REQUIRED: 'Payment date is required',
            ERROR_DATE_INVALID: 'Payment date must be a valid date',
            ERROR_DATE_MIN: 'Payment date must be after the previous milestone date',
            ERROR_DATE_MAX: 'Payment date must be on or before the contract end date',
            ERROR_REENTER_MILESTONE_DATES:
                'One or more payment milestone dates fall outside the contract period. Please update the dates via Edit Payment Milestones so all dates are between the Start Date and End Date.',
        },
        ADD_MILESTONE_MODAL_WIDTH: '900px',
    },
    PAYMENT_MILESTONE_LIMITS: {
        MIN_ROWS: 1,
        MAX_ROWS: 100,
        GST_RATE: 0.18,
        CONTRACT_VALUE_MAX: 999999999,
        CONTRACT_VALUE_MIN: 1,
        // Percent and amount should never be zero for a milestone row
        PERCENT_MIN: 0.01,
        PERCENT_MAX: 100,
        AMOUNT_MIN: 0.01,
        DELIVERABLE_NAME_MAX_LENGTH: 200,
        NUMERIC_EQUALITY_TOLERANCE: 0.01,
    },
    ROUTES: {
        SHIKSHA_DASHBOARD: '/crm/view-client-shiksha',
        shikshaFunderDetail: (funderId: number): string => `/crm/shiksha/${funderId}`,
        dealDetail: (leadId: number): string => `/crm/shiksha/deal/${leadId}`,
        DEAL_DETAIL_PREFIX: '/crm/shiksha/deal/',
    },
    UI: {
        COMMON: {
            BUTTONS: {
                CANCEL: LABEL_CANCEL,
                SAVE: LABEL_SAVE,
                CREATE: 'Create',
                BACK: 'Back',
                CLOSE: LABEL_CLOSE,
                SAVING: 'Saving...',
                CREATING: 'Creating...',
                SUBMIT: 'Submit',
                CONTINUE: 'Continue',
                BYPASS_EMAIL_DOMAIN_TYPO: 'Bypass Email Domain Typo',
                BYPASS_EMAIL_DOMAIN_TYPO_TOOLTIP: 'Bypass email domain typo validation',
            },
            ARIA_LABELS: {
                CLOSE_DIALOG: LABEL_CLOSE,
                GO_BACK: 'Go back',
                CLEAR_SEARCH: 'Clear search',
                EDIT: 'Edit',
                SAVE: LABEL_SAVE,
                CANCEL: LABEL_CANCEL,
            },
            ICONS: {
                CLOSE: 'close',
                ARROW_BACK: 'arrow_back',
                SEARCH: 'search',
                INFO: 'info',
                SYNC: 'sync',
                SEARCH_OFF: 'search_off',
            },
            VALUES: {
                NOT_AVAILABLE: '-',
                COUNTRY_CODE: '+91',
                COUNTRY_CODE_NUMBER: 91,
                CURRENCY_SYMBOL: '₹',
                CALL_CONNECTED: 'Call Connected',
            },
            CONTACT_CAROUSEL: {
                AUTO_CAROUSEL_INTERVAL_MS: 5000,
            },
        },
        CONTACT_BOOK: {
            LABELS: {
                CONTACT_BOOK_TITLE: 'Contact Book',
                CREATE_CONTACT_MODAL_TITLE: 'Create Contact',
                EDIT_CONTACT_MODAL_TITLE: 'Edit Contact',
                VIEW_ALL_CONTACTS_MODAL_TITLE: 'Contact Book',
                VIEW_ALL_CONTACTS_BUTTON: 'View All Contacts',
                VIEW_CONTACTS_BUTTON: 'View Contacts',
                CONTACT_NAME_FIELD: 'Name',
                CONTACT_DESIGNATION_FIELD: 'Designation',
                CONTACT_CUSTOM_DESIGNATION_FIELD: 'Custom Designation',
                CONTACT_EMAIL_FIELD: 'Email',
                CONTACT_PHONE_NUMBER_FIELD: 'Mobile Number',
                CONTACT_LINKEDIN_URL_FIELD: 'LinkedIn URL',
                CONTACT_IS_POC_FIELD: 'Is Point of Contact?',
                CONTACT_PRIMARY_BADGE: 'POC',
                CONTACT_PRIMARY_BADGE_TOOLTIP: 'Point of Contact',
                CONTACT_LINKEDIN_LINK_TEXT: 'LinkedIn',
                ADD_CONTACT_BUTTON: 'Add Contact',
            },
            PLACEHOLDERS: {
                SELECT_DESIGNATION_DROPDOWN: 'Select Designation',
                ENTER_NAME_INPUT: 'Enter name',
                ENTER_EMAIL_INPUT: 'Enter email',
                ENTER_CUSTOM_DESIGNATION_INPUT: 'Enter designation',
                ENTER_PHONE_NUMBER_INPUT: 'Enter mobile number',
                ENTER_LINKEDIN_URL_INPUT: 'https://www.linkedin.com/in/profile-name',
                SELECT_POC_DROPDOWN: 'Select',
                SEARCH_CONTACTS_INPUT: 'Search by name',
            },
            MESSAGES: {
                LOADING_CONTACTS: 'Loading contacts...',
                LOADING_DESIGNATIONS: 'Loading designations...',
                NO_CONTACTS_AVAILABLE:
                    RECORD_COMMUNICATION_SHARED_PLACEHOLDERS.NO_CONTACTS_AVAILABLE,
                NO_CONTACTS_MESSAGE: 'There is no contact. Please add a new contact',
                NO_CONTACTS_MATCHING_SEARCH:
                    "We couldn't find any contacts matching your search. Try adjusting your search terms.",
                NO_CONTACTS_CLICK_ADD:
                    'No contacts available. Click "Add Contact" to create your first contact.',
                POC_INFO_MESSAGE: COMMON_NOTES.POC_ONLY_ONE,
                POC_FIRST_TIME_MESSAGE: 'This will be set as the Point of Contact.',
                /** Appended after “{Name}” or “{Name} ({designation})” in Contact Book POC hint */
                POC_IS_CURRENT_SUFFIX: COMMON_NOTES.POC_IS_CURRENT_SUFFIX,
                EMAIL_OR_PHONE_INFO_MESSAGE: 'Either email or mobile number must be provided.',
                DELETE_CONFIRMATION_TITLE: 'Delete Contact',
                deleteConfirmationMessage: (contactName: string): string =>
                    `Are you sure you want to delete ${contactName}? This action cannot be undone.`,
                DELETE_CONFIRMATION_CONFIRM_TEXT: 'Delete',
                DELETE_CONFIRMATION_CANCEL_TEXT: LABEL_CANCEL,
            },
            ARIA_LABELS: {
                EDIT_CONTACT_BUTTON: 'Edit contact',
                DELETE_CONTACT_BUTTON: 'Delete contact',
                PREVIOUS_CONTACT_BUTTON: 'Previous contact',
                NEXT_CONTACT_BUTTON: 'Next contact',
                goToContactDot: (contactNumber: number): string => `Go to contact ${contactNumber}`,
            },
            ICONS: {
                PERSON_ADD: 'person_add',
                EMAIL: 'email',
                PHONE: 'smartphone',
                LINK: 'link',
                EDIT: 'edit',
                DELETE: 'delete',
            },
        },
        DEAL_FORM: {
            LABELS: {
                CREATE_DEAL_MODAL_TITLE_PREFIX: 'Create Deal -',
                CREATE_DEAL_BUTTON: 'Create Deal',
                DEAL_NAME_FIELD: 'Deal Name',
                INTERESTED_PRODUCTS_FIELD: 'Interested products',
                FINANCIAL_YEAR_FIELD: 'Financial Year',
                ENGAGEMENT_TYPE_FIELD: 'Engagement Type',
                SOURCE_FIELD: 'Source',
                DEAL_PROJECTIONS_FIELD: 'Deal Projections',
                ADDITIONAL_NOTE_FIELD: 'Additional Note',
            },
            PLACEHOLDERS: {
                DEAL_NAME_INPUT: 'Deal Name',
                SELECT_FINANCIAL_YEAR_DROPDOWN: 'Select Financial Year',
                SELECT_ENGAGEMENT_TYPE_DROPDOWN: 'Select Engagement Type',
                SELECT_SOURCE_DROPDOWN: 'Select Source',
                ENTER_AMOUNT_INPUT: 'Enter amount',
                ENTER_ADDITIONAL_NOTES_TEXTAREA: 'Enter additional notes...',
            },
            MESSAGES: {
                DEAL_NAME_AUTO_GENERATED_INFO:
                    'Deal name is auto-generated based on client name, year and selected products. This deal will be auto-assigned to the creator.',
                TENDER_RFP_LABEL: 'Tender (RFP)',
            },
        },
        BASIC_INFORMATION: {
            LABELS: {
                BASIC_INFORMATION_TITLE: 'Basic Information',
                CLIENT_TYPE_FIELD: 'Type',
                CLIENT_NAME_FIELD: 'Name',
                INTERESTED_PRODUCT_FIELD: 'Interested Product',
                CLIENT_WEBSITE_FIELD: 'Website',
                CLIENT_DESCRIPTION_FIELD: 'Note',
            },
            MESSAGES: {
                NO_INFORMATION_AVAILABLE: 'No information available',
            },
            TOOLTIPS: {
                CLIENT_TYPE_INFO:
                    'The client type classification (e.g., NGO, PSU, Corporate Foundation, Government)',
                INTERESTED_PRODUCT_INFO:
                    'Products that the client has expressed interest in purchasing',
            },
            TARGET_ATTRIBUTES: {
                BLANK: '_blank',
                NOOPENER_NOREFERRER: 'noopener noreferrer',
            },
        },
        HEADER: {
            LABELS: {
                CREATE_DEAL_BUTTON: 'Create Deal',
            },
            TOOLTIPS: {
                DEAL_TYPE_TAG: 'Deal type',
            },
            ARIA_LABELS: {
                BACK_BUTTON: 'Go back',
            },
            ICONS: {
                ARROW_BACK: 'arrow_back',
            },
        },
        FILTER: {
            LABELS: {
                FINANCIAL_YEAR_DROPDOWN: 'Financial Year',
                STAGE_DROPDOWN: 'Stage',
                STATUS_TYPE_DROPDOWN: 'Status Type',
            },
            PLACEHOLDERS: {
                SELECT_FINANCIAL_YEAR_DROPDOWN: 'Select Financial Year',
                SELECT_STAGE_DROPDOWN: 'Select Stage',
                SELECT_STATUS_TYPE_DROPDOWN: 'Select Status Type',
            },
        },
        MAIN_PAGE: {
            LABELS: {
                FUNDER_NOT_FOUND_TITLE: 'Funder Not Found',
                GO_TO_DASHBOARD_BUTTON: 'Go to Dashboard',
                DEALS_SECTION_TITLE: 'Deals',
                DEAL_COUNT_LABELS: {
                    SINGULAR: 'Deal',
                    PLURAL: 'Deals',
                } as const,
            },
            MESSAGES: {
                SOMETHING_WENT_WRONG: 'Something went wrong',
                NO_DEALS_CREATED_YET: 'No deals created yet',
                CREATE_FIRST_DEAL_MESSAGE:
                    'Create the first deal to start engaging with this customer',
                NO_DEALS_FOUND: 'No deals found',
                NO_DEALS_MATCH_FILTERS:
                    'No deals match the selected filters. Try adjusting your filter criteria.',
            },
            BUTTONS: {
                CREATE_FIRST_DEAL: 'Create First Deal',
            },
            ICONS: {
                ERROR_OUTLINE: 'error_outline',
                NO_DEALS_YET: 'business',
                SEARCH_OFF: 'search_off',
            },
            ARIA_LABELS: {
                getViewDealCardAriaLabel: (dealName: string): string => `View deal ${dealName}`,
            },
            TARGET_ATTRIBUTES: {
                BLANK: '_blank',
                NOOPENER_NOREFERRER: 'noopener noreferrer',
            },
            SKELETON: {
                LINE_WIDTHS: {
                    SMALL_MEDIUM_SMALL: ['m', 'm', 's'],
                    SMALL_XLARGE_MEDIUM_FULL: ['s', 'xl', 'm', 'm', 'full'],
                    FULL: ['full'],
                    MEDIUM_SMALL: ['m', 's'],
                },
            },
        },
        DEAL_MODAL: {
            LABELS: {
                CREATE_DEAL_TITLE: 'Create Deal',
                CHOOSE_DEAL_TYPE_PROMPT: 'Choose deal type to start',
                DIRECT_SALES_TITLE: 'Direct Sales',
                TENDER_TITLE: 'Tender (RFP)',
                COMING_SOON_BADGE: 'Coming Soon',
            },
            DESCRIPTIONS: {
                DIRECT_SALES_DESCRIPTION: 'Standard Mindspark & Assessment direct-sales workflow',
                TENDER_DESCRIPTION: 'RFP / Tender workflow',
            },
            ICONS: {
                LOCAL_OFFER: 'local_offer',
                GAVEL: 'gavel',
            },
        },
        CONTACT_CAROUSEL: {
            ARIA_LABELS: {
                PREVIOUS_CONTACT: 'Previous contact',
                NEXT_CONTACT: 'Next contact',
            },
            ICONS: {
                CHEVRON_LEFT: 'chevron_left',
                CHEVRON_RIGHT: 'chevron_right',
            },
        },
    },
} as const

// Deal detail component constants - uses references from main constants
export const getDealDetailComponentConstants = () => ({
    dashboardRoute: SHIKSHA_FUNDER_DETAIL_CONSTANTS.ROUTES.SHIKSHA_DASHBOARD,
    dialogMaxWidth: SHIKSHA_FUNDER_DETAIL_CONSTANTS.DIALOG.MAX_WIDTH,
    dialogBackdropClass: SHIKSHA_FUNDER_DETAIL_CONSTANTS.DIALOG.BACKDROP_CLASS,
    dialogWidth: SHIKSHA_FUNDER_DETAIL_CONSTANTS.DEAL_DETAIL.DIALOG_WIDTH,
    recordCommunicationModalWidth:
        SHIKSHA_FUNDER_DETAIL_CONSTANTS.DIALOG.RECORD_COMMUNICATION_MODAL_WIDTH,
    recordCommunicationModalMaxWidth:
        SHIKSHA_FUNDER_DETAIL_CONSTANTS.DIALOG.RECORD_COMMUNICATION_MODAL_MAX_WIDTH,
    stickyHeaderOffset: SHIKSHA_FUNDER_DETAIL_CONSTANTS.DEAL_DETAIL.STICKY_HEADER_OFFSET,
    notAvailable: SHIKSHA_FUNDER_DETAIL_CONSTANTS.UI.COMMON.VALUES.NOT_AVAILABLE,
    loadingMessage: SHIKSHA_FUNDER_DETAIL_CONSTANTS.DEAL_DETAIL.MESSAGES.LOADING,
    defaultDealType: SHIKSHA_FUNDER_DETAIL_CONSTANTS.DEAL_FORM.DEAL_TYPE.DIRECT_SALES_VALUE,
})

// Financial year generation utility constants
export const FINANCIAL_YEAR_CONSTANTS = {
    MONTH_THRESHOLD: 4, // April (0-indexed: 3, but we use 1-indexed: 4)
    CURRENT_YEAR_OFFSET: 0,
} as const
